import { useState, useEffect, useRef, useCallback } from 'react';
/* eslint-disable react/no-unstable-nested-components */
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import {
  Book, ChevronLeft, ChevronRight, Play, Pause, Check,
  Settings, Eye, EyeOff, Search, X, BookCheck, Bookmark,
  BookmarkCheck, Maximize2, Minimize2, Hash, Layers, Volume2
} from 'lucide-react';
import {
  getQuranSurahs,
  getQuranReciters,
  getQuranTranslations,
  refreshQuranTranslations,
  getArabicFontStyles,
  getArabicEditionForStyle,
  getQuranAyahWithCustomTranslation,
  getQuranSurahWithCustomTranslation,
  getQuranByPage,
  getQuranByJuz,
  getQuranUserPreferences,
  updateQuranUserPreferences,
  getQuranBookmarks,
  addQuranBookmark,
  deleteQuranBookmark,
  logQuranReadingForBereket,
  logQuranListeningForBereket,
  addQuranListeningMinutes,
  searchQuranByKeyword,
  getReadAyahs,
  markAyahAsReadInDB,
  unmarkAyahAsReadInDB
} from '../lib/api';

const RECITER_AUDIO_QUALITY = {
  'ar.abdulbasitmurattal': 64,
  'ar.abdurrahmaansudais': 64,
  'ar.shaatree': 64,
  'ar.husary': 64,
  'ar.minshawi': 128,
  'ar.muhammadayyoub': 128,
  'ar.muhammadjibreel': 128,
  'ar.alafasy': 128,
};

const RECITERS_WITHOUT_STABLE_AUDIO = new Set(['ar.azeezalili']);
const DEFAULT_ARABIC_FONT_SIZE = 28;

const AYAH_NUMBER_GLYPHS = [
  '۱','۲','۳','۴','۵','۶','۷','۸','۹','۱۰',
  '۱۱','۱۲','۱۳','۱۴','۱۵','۱۶','۱۷','۱۸','۱۹','۲۰',
];

function AyahCircle({ num }) {
  const display = num <= 20 ? AYAH_NUMBER_GLYPHS[num - 1] : String(num);
  return (
    <span
      className="inline-flex items-center justify-center mx-1 align-middle"
      style={{
        width: '1.6em',
        height: '1.6em',
        border: '1.5px solid #78716c',
        borderRadius: '50%',
        fontSize: '0.55em',
        color: '#57534e',
        lineHeight: 1,
        verticalAlign: 'middle',
        flexShrink: 0,
      }}
    >
      {display}
    </span>
  );
}

function MushafAyahMarker({ num }) {
  const display = num <= 20 ? AYAH_NUMBER_GLYPHS[num - 1] : String(num);
  return (
    <span className="mushaf-ayah-marker" style={{ display: 'inline-flex' }}>
      {display}
    </span>
  );
}

function ModalPicker({ isOpen, onClose, title, items, selectedValue, onSelect, searchable = false, renderItem, searchPlaceholder }) {
  const [searchTerm, setSearchTerm] = useState('');
  if (!isOpen) return null;

  const filteredItems = searchable
    ? items.filter(item => {
        const searchText = typeof item === 'string' ? item : (item.name || item.label || item.englishName || '').toLowerCase();
        return searchText.includes(searchTerm.toLowerCase());
      })
    : items;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center p-4 border-b border-stone-200">
          <h3 className="text-lg font-semibold text-stone-800">{title}</h3>
          <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-stone-400" />
          </button>
        </div>
        {searchable && (
          <div className="p-4 border-b border-stone-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={searchPlaceholder || 'Search...'}
                className="w-full pl-10 pr-4 py-2 bg-stone-50 border border-stone-200 rounded-lg outline-none focus:ring-2 focus:ring-emerald-500/20"
                autoFocus
              />
            </div>
          </div>
        )}
        <div className="overflow-y-auto flex-1 p-2">
          {filteredItems.length === 0 ? (
            <p className="text-center text-stone-400 py-8">No items found</p>
          ) : (
            filteredItems.map((item, index) => {
              const value = typeof item === 'object' ? (item.value || item.id || item.number || item.identifier) : item;
              const isSelected = value === selectedValue;
              return (
                <button
                  key={index}
                  onClick={() => { onSelect(value); onClose(); }}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-all ${isSelected ? 'bg-emerald-600 text-white font-medium' : 'hover:bg-stone-50 text-stone-800'}`}
                >
                  {renderItem ? renderItem(item, isSelected) : (typeof item === 'object' ? (item.label || item.name || item.englishName) : item)}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

export default function QuranReader({ user }) {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const audioRef = useRef(null);
  const activeAyahRef = useRef(null);

  const [surahs, setSurahs] = useState([]);
  const [selectedSura, setSelectedSura] = useState(1);
  const [selectedAya, setSelectedAya] = useState(1);
  const [startAya, setStartAya] = useState(1);
  const [endAya, setEndAya] = useState(1);
  const [selectedPage, setSelectedPage] = useState(1);
  const [selectedJuz, setSelectedJuz] = useState(1);

  const [ayahData, setAyahData] = useState(null);
  const [surahData, setSurahData] = useState(null);
  const [rangeData, setRangeData] = useState([]);
  const [pageData, setPageData] = useState(null);
  const [juzData, setJuzData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [degradedNotice, setDegradedNotice] = useState('');

  const [bookmarks, setBookmarks] = useState([]);
  const [showBookmarks, setShowBookmarks] = useState(false);
  const [bookmarkSearchQuery, setBookmarkSearchQuery] = useState('');
  const [showQuranSearch, setShowQuranSearch] = useState(false);
  const [quranSearchQuery, setQuranSearchQuery] = useState('');
  const [quranSearchResults, setQuranSearchResults] = useState([]);
  const [quranSearching, setQuranSearching] = useState(false);

  const [preferences, setPreferences] = useState({
    preferred_reciter: 'ar.alafasy',
    preferred_translation: 'bs.korkut',
    show_translation: true,
    show_arabic: true,
    reading_mode: 'ayah',
    arabic_font_style: 'uthmani',
    font_size: 28,
    focused_mode: true
  });

  const [arabicTextWeight, setArabicTextWeight] = useState(() => {
    if (typeof window === 'undefined') return 'normal';
    return localStorage.getItem('quran_arabic_text_weight') || 'normal';
  });

  const [arabicTextSpacing, setArabicTextSpacing] = useState(() => {
    if (typeof window === 'undefined') return 'normal';
    return localStorage.getItem('quran_arabic_text_spacing') || 'normal';
  });

  const [showSettings, setShowSettings] = useState(false);
  const [showSurahPicker, setShowSurahPicker] = useState(false);
  const [showReciterPicker, setShowReciterPicker] = useState(false);
  const [showTranslationPicker, setShowTranslationPicker] = useState(false);
  const [showFontStylePicker, setShowFontStylePicker] = useState(false);
  const [showRangeSelector, setShowRangeSelector] = useState(false);
  const [showPageSelector, setShowPageSelector] = useState(false);
  const [showJuzSelector, setShowJuzSelector] = useState(false);
  
  // Temporary input values for selectors (allow empty string during typing)
  const [pageInputValue, setPageInputValue] = useState('1');
  const [juzInputValue, setJuzInputValue] = useState('1');
  const [startAyaInput, setStartAyaInput] = useState('1');
  const [endAyaInput, setEndAyaInput] = useState('1');

  const [reciters, setReciters] = useState([]);
  const [translations, setTranslations] = useState([]);
  const [fontStyles, setFontStyles] = useState([]);


  const [audioUrl, setAudioUrl] = useState(null);
  const [ayahAudioUrls, setAyahAudioUrls] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasListened, setHasListened] = useState(false);
  const [currentPlayingAyah, setCurrentPlayingAyah] = useState(null);
  const [activeAyahMeta, setActiveAyahMeta] = useState(null);
  const [ayahCount, setAyahCount] = useState(0);
  const [perAyahMode, setPerAyahMode] = useState(false);

  // Long-press state for ayah actions
  const [longPressAyah, setLongPressAyah] = useState(null);
  const [showAyahMenu, setShowAyahMenu] = useState(false);
  const longPressTimer = useRef(null);
  const longPressTriggeredRef = useRef(false);
  const touchTapHandledRef = useRef(false);
  const lastManualPlayAtRef = useRef(0);
  const [useLegacyMobileMushafLayout, setUseLegacyMobileMushafLayout] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 640;
  });
  const [readAyahs, setReadAyahs] = useState(new Set());
  const [lastClickedAyahIdx, setLastClickedAyahIdx] = useState(0);

  const registerListeningSession = useCallback(() => {
    if (hasListened) return;

    Promise.allSettled([
      logQuranListeningForBereket(selectedSura),
      addQuranListeningMinutes(1),
    ]).then((results) => {
      const bereketResult = results[0];
      if (bereketResult?.status === 'fulfilled' && bereketResult.value?.points) {
        toast.success(`+${bereketResult.value.points} ${lang === 'hr' ? 'Bereket bodova!' : 'Bereket points!'}`);
      }
      if (bereketResult?.status === 'rejected') {
        console.error('Error logging Quran listening points:', bereketResult.reason);
      }

      const minutesResult = results[1];
      if (minutesResult?.status === 'rejected') {
        console.error('Error logging Quran listening minutes:', minutesResult.reason);
      }
    });

    setHasListened(true);
  }, [hasListened, selectedSura, lang]);

  useEffect(() => {
    fetchSurahs();
    fetchReciters();
    fetchTranslations();
    fetchFontStyles();
    fetchPreferences();
    fetchBookmarks();
    fetchReadAyahs();
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobileViewport(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const activeFontSize = preferences.font_size || DEFAULT_ARABIC_FONT_SIZE;
    if (activeFontSize !== DEFAULT_ARABIC_FONT_SIZE) {
      setUseLegacyMobileMushafLayout(true);
    }
  }, [preferences.font_size]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem('quran_arabic_text_weight', arabicTextWeight);
  }, [arabicTextWeight]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem('quran_arabic_text_spacing', arabicTextSpacing);
  }, [arabicTextSpacing]);

  // Fetch read ayahs from database
  const fetchReadAyahs = async () => {
    try {
      const data = await getReadAyahs();
      const readSet = new Set(data.map(item => `${item.surah_number}:${item.ayah_number}`));
      setReadAyahs(readSet);
    } catch (error) {
      console.error('Error fetching read ayahs:', error);
    }
  };

  useEffect(() => {
    fetchContent();
  }, [selectedSura, selectedAya, startAya, endAya, selectedPage, selectedJuz, preferences.reading_mode, preferences.preferred_translation, preferences.preferred_reciter, preferences.arabic_font_style]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleEnded = () => {
      if (Date.now() - lastManualPlayAtRef.current < 350) {
        return;
      }

      if (perAyahMode && currentPlayingAyah !== null && ayahAudioUrls.length > 0) {
        const nextIdx = currentPlayingAyah + 1;
        if (nextIdx < ayahAudioUrls.length) {
          setCurrentPlayingAyah(nextIdx);
          const nextMeta = getAyahMetaByIndex(nextIdx);
          if (nextMeta) setActiveAyahMeta(nextMeta);
          const nextUrl = ayahAudioUrls[nextIdx];
          if (nextUrl) {
            audio.src = nextUrl;
            audio.load();
            audio.play().catch(() => {});
          }
        } else {
          setIsPlaying(false);
          setCurrentPlayingAyah(null);
        }
      } else {
        setIsPlaying(false);
        setCurrentPlayingAyah(null);
      }
    };

    const handleTimeUpdate = () => {
      if (perAyahMode) return;
      if (!ayahCount || ayahCount <= 1) return;
      const audio = audioRef.current;
      if (!audio || !audio.duration || isNaN(audio.duration)) return;
      const perAyah = audio.duration / ayahCount;
      const idx = Math.min(Math.floor(audio.currentTime / perAyah), ayahCount - 1);
      setCurrentPlayingAyah(idx);
      const playingMeta = getAyahMetaByIndex(idx);
      if (playingMeta) setActiveAyahMeta(playingMeta);
    };

    const handleError = () => {
      if (currentPlayingAyah === null) {
        setIsPlaying(false);
        return;
      }

      const failedUrl = ayahAudioUrls[currentPlayingAyah];
      if (!failedUrl || failedUrl.includes('/ar.alafasy/')) {
        setIsPlaying(false);
        toast.error(lang === 'hr' ? 'Audio nije dostupan za ovaj ajet' : 'Audio is not available for this ayah');
        return;
      }

      const fallbackUrl = failedUrl.replace(/\/audio\/\d+\/[^/]+\//, '/audio/128/ar.alafasy/');
      setAyahAudioUrls((prev) => {
        const next = [...prev];
        next[currentPlayingAyah] = fallbackUrl;
        return next;
      });
      audio.src = fallbackUrl;
      audio.load();
      audio.play().catch(() => {
        setIsPlaying(false);
      });
    };

    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('error', handleError);
    };
  }, [ayahCount, perAyahMode, currentPlayingAyah, ayahAudioUrls, lang, getAyahMetaByIndex]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (perAyahMode) {
      if (!isPlaying) {
        audio.pause();
      }
      return;
    }

    const handlePlayback = async () => {
      try {
        if (audioUrl && isPlaying) {
          if (audio.src !== audioUrl) {
            audio.src = audioUrl;
            audio.load();
          }
          await audio.play();
          if (preferences.reading_mode === 'ayah') setCurrentPlayingAyah(0);
        } else {
          audio.pause();
          if (!isPlaying) setCurrentPlayingAyah(null);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Audio playback error:', error);
          setIsPlaying(false);
        }
      }
    };
    handlePlayback();
  }, [audioUrl, isPlaying, perAyahMode, preferences.reading_mode]);

  useEffect(() => {
    if (activeAyahRef.current) {
      activeAyahRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [currentPlayingAyah]);

  useEffect(() => {
    if (!showBookmarks) {
      setBookmarkSearchQuery('');
    }
  }, [showBookmarks]);

  useEffect(() => {
    if (!showQuranSearch) {
      setQuranSearchQuery('');
      setQuranSearchResults([]);
      setQuranSearching(false);
    }
  }, [showQuranSearch]);

  useEffect(() => {
    if (!showTranslationPicker) return;

    let mounted = true;
    refreshQuranTranslations()
      .then((list) => {
        if (!mounted) return;
        if (Array.isArray(list) && list.length > 0) {
          setTranslations(list);
        }
      })
      .catch((error) => {
        console.error('Translation refresh failed:', error);
      });

    return () => {
      mounted = false;
    };
  }, [showTranslationPicker]);

  const fetchSurahs = async () => {
    try {
      const data = await getQuranSurahs();
      setSurahs(data);
    } catch (error) {
      toast.error(lang === 'hr' ? 'Greška pri učitavanju sura' : 'Error loading surahs');
    }
  };

  const fetchReciters = () => setReciters(getQuranReciters());
  const fetchTranslations = () => {
    setTranslations(getQuranTranslations());
  };
  const fetchFontStyles = () => setFontStyles(getArabicFontStyles());

  const fetchPreferences = async () => {
    try {
      const prefs = await getQuranUserPreferences();
      setPreferences({
        ...prefs,
        preferred_translation: prefs?.preferred_translation || 'bs.korkut',
        focused_mode: true,
      });
      if (prefs.last_surah) setSelectedSura(prefs.last_surah);
      if (prefs.last_ayah) setSelectedAya(prefs.last_ayah);
    } catch (error) {
      console.error('Error fetching preferences:', error);
    }
  };

  const fetchBookmarks = async () => {
    try {
      const data = await getQuranBookmarks();
      setBookmarks(data);
    } catch (error) {
      console.error('Error fetching bookmarks:', error);
    }
  };

  const getSafeReciter = useCallback((reciterId) => {
    const candidate = reciterId || 'ar.alafasy';
    return RECITERS_WITHOUT_STABLE_AUDIO.has(candidate) ? 'ar.alafasy' : candidate;
  }, []);

  const buildAyahAudioUrl = useCallback((ayahNumber, reciterId = preferences.preferred_reciter) => {
    const safeReciter = getSafeReciter(reciterId);
    const quality = RECITER_AUDIO_QUALITY[safeReciter] || 128;
    return `https://cdn.islamic.network/quran/audio/${quality}/${safeReciter}/${ayahNumber}.mp3`;
  }, [getSafeReciter, preferences.preferred_reciter]);

  function getAyahMetaByIndex(idx) {
    if (idx === null || idx === undefined || idx < 0) return null;
    const selectedSurahObj = surahs.find((surah) => surah.number === selectedSura);

    if (preferences.reading_mode === 'ayah' && ayahData) {
      return {
        mode: 'ayah',
        surahNumber: ayahData.surah?.number || selectedSura,
        surahName: ayahData.surah?.englishName || selectedSurahObj?.englishName,
        ayahNumber: ayahData.numberInSurah || selectedAya,
        pageNumber: ayahData.page,
      };
    }

    if (preferences.reading_mode === 'range' && rangeData[idx]) {
      return {
        mode: 'range',
        surahNumber: selectedSura,
        surahName: selectedSurahObj?.englishName,
        ayahNumber: startAya + idx,
        rangeStart: startAya,
        rangeEnd: endAya,
        pageNumber: rangeData[idx]?.page,
      };
    }

    if (preferences.reading_mode === 'surah' && surahData?.ayahs?.[idx]) {
      return {
        mode: 'surah',
        surahNumber: selectedSura,
        surahName: surahData.englishName || selectedSurahObj?.englishName,
        ayahNumber: surahData.ayahs[idx].numberInSurah,
        pageNumber: surahData.ayahs[idx].page,
      };
    }

    if (preferences.reading_mode === 'page' && pageData?.ayahs?.[idx]) {
      const ayah = pageData.ayahs[idx];
      return {
        mode: 'page',
        surahNumber: ayah.surah?.number || ayah.surahNumber,
        surahName: ayah.surah?.englishName,
        ayahNumber: ayah.numberInSurah,
        pageNumber: ayah.page || selectedPage,
      };
    }

    if (preferences.reading_mode === 'juz' && juzData?.ayahs?.[idx]) {
      const ayah = juzData.ayahs[idx];
      return {
        mode: 'juz',
        surahNumber: ayah.surah?.number || ayah.surahNumber,
        surahName: ayah.surah?.englishName,
        ayahNumber: ayah.numberInSurah,
        pageNumber: ayah.page,
        juzNumber: selectedJuz,
      };
    }

    return null;
  }

  function findAyahIndex(surahNum, ayahNum) {
    if (!surahNum || !ayahNum) return -1;

    if (preferences.reading_mode === 'ayah') {
      return selectedSura === surahNum && selectedAya === ayahNum ? 0 : -1;
    }

    if (preferences.reading_mode === 'range') {
      if (selectedSura !== surahNum) return -1;
      if (ayahNum < startAya || ayahNum > endAya) return -1;
      return ayahNum - startAya;
    }

    if (preferences.reading_mode === 'surah') {
      if (selectedSura !== surahNum || !surahData?.ayahs?.length) return -1;
      return surahData.ayahs.findIndex((ayah) => ayah.numberInSurah === ayahNum);
    }

    if (preferences.reading_mode === 'page') {
      if (!pageData?.ayahs?.length) return -1;
      return pageData.ayahs.findIndex((ayah) => {
        const pageSurahNum = ayah.surah?.number || ayah.surahNumber;
        return pageSurahNum === surahNum && ayah.numberInSurah === ayahNum;
      });
    }

    if (preferences.reading_mode === 'juz') {
      if (!juzData?.ayahs?.length) return -1;
      return juzData.ayahs.findIndex((ayah) => {
        const juzSurahNum = ayah.surah?.number || ayah.surahNumber;
        return juzSurahNum === surahNum && ayah.numberInSurah === ayahNum;
      });
    }

    return -1;
  }

  const fetchContent = async () => {
    setLoading(true);
    setCurrentPlayingAyah(null);
    setIsPlaying(false);
    setDegradedNotice('');

    const edition = getArabicEditionForStyle(preferences.arabic_font_style);

    const applyDegradedNotice = (payload) => {
      console.log('[QuranReader] applyDegradedNotice called with:', {
        hasDegradedFlag: !!payload?.degraded,
        degradedSource: payload?.degraded_source,
        degradedMessage: payload?.degraded_message
      });
      
      if (!payload?.degraded) {
        setDegradedNotice('');
        return;
      }

      const fromCache = payload.degraded_source === 'cache';
      const noticeText = fromCache
        ? (lang === 'hr'
            ? 'Prikazan je zadnji spremljeni sadržaj (cache) zbog privremenog API/mrežnog problema.'
            : 'Showing last saved cached content due to temporary API/network issues.')
        : (lang === 'hr'
            ? 'Prikazan je lokalni fallback (Al-Fatiha + Besim Korkut) dok se veza ne stabilizuje.'
            : 'Showing local fallback (Al-Fatihah + Besim Korkut) until the connection stabilizes.');
      
      console.log('[QuranReader] Setting degraded notice:', noticeText);
      setDegradedNotice(noticeText);
    };

    try {
      if (preferences.reading_mode === 'ayah') {
        const data = await getQuranAyahWithCustomTranslation(selectedSura, selectedAya, preferences.preferred_translation, edition !== 'quran-uthmani' ? edition : null);
        applyDegradedNotice(data);
        setAyahData(data);
        const firstAyahAudio = buildAyahAudioUrl(data.number, preferences.preferred_reciter);
        setAudioUrl(firstAyahAudio);
        setAyahCount(1);
        setPerAyahMode(true);
        setAyahAudioUrls([firstAyahAudio]);
      } else if (preferences.reading_mode === 'range') {
        const surahBundle = await getQuranSurahWithCustomTranslation(
          selectedSura,
          preferences.preferred_translation,
          edition !== 'quran-uthmani' ? edition : null
        );
        applyDegradedNotice(surahBundle);
        const data = (surahBundle?.ayahs || []).filter(
          (ayah) => ayah.numberInSurah >= startAya && ayah.numberInSurah <= endAya
        );
        setRangeData(data);
        const urls = data.map(a => buildAyahAudioUrl(a.number, preferences.preferred_reciter));
        setAyahAudioUrls(urls);
        if (urls.length > 0) setAudioUrl(urls[0]);
        setAyahCount(data.length);
        setPerAyahMode(true);
      } else if (preferences.reading_mode === 'surah') {
        const data = await getQuranSurahWithCustomTranslation(selectedSura, preferences.preferred_translation, edition !== 'quran-uthmani' ? edition : null);
        applyDegradedNotice(data);
        setSurahData(data);
        if (data?.ayahs) {
          const urls = data.ayahs.map(a => buildAyahAudioUrl(a.number, preferences.preferred_reciter));
          setAyahAudioUrls(urls);
          setAudioUrl(urls[0]);
          setAyahCount(data.ayahs.length);
          setPerAyahMode(true);
        }
      } else if (preferences.reading_mode === 'page') {
        const data = await getQuranByPage(selectedPage, preferences.preferred_translation, edition);
        applyDegradedNotice(data);
        setPageData(data);
        if (data?.ayahs?.length > 0) {
          const urls = data.ayahs.map(a => buildAyahAudioUrl(a.number, preferences.preferred_reciter));
          setAyahAudioUrls(urls);
          setAudioUrl(urls[0]);
          setAyahCount(data.ayahs.length);
          setPerAyahMode(true);
        }
      } else if (preferences.reading_mode === 'juz') {
        const data = await getQuranByJuz(selectedJuz, preferences.preferred_translation, edition);
        applyDegradedNotice(data);
        setJuzData(data);
        if (data?.ayahs?.length > 0) {
          const urls = data.ayahs.map(a => buildAyahAudioUrl(a.number, preferences.preferred_reciter));
          setAyahAudioUrls(urls);
          setAudioUrl(urls[0]);
          setAyahCount(data.ayahs.length);
          setPerAyahMode(true);
        }
      }
    } catch (error) {
      setDegradedNotice(lang === 'hr' ? 'Sadržaj trenutno nije dostupan. Pokušaj ponovo za nekoliko trenutaka.' : 'Content is currently unavailable. Please retry shortly.');
      toast.error(lang === 'hr' ? 'Greška pri učitavanju' : 'Error loading content');
    } finally {
      setLoading(false);
    }
  };

  const playAyah = (idx) => {
    const audio = audioRef.current;
    if (!audio || !ayahAudioUrls[idx]) return;

    setLastClickedAyahIdx(idx);
    const selectedMeta = getAyahMetaByIndex(idx);
    if (selectedMeta) setActiveAyahMeta(selectedMeta);

    if (currentPlayingAyah === idx && isPlaying) {
      audio.pause();
      audio.currentTime = 0;
      setIsPlaying(false);
      return;
    }

    if (currentPlayingAyah === idx && !isPlaying) {
      audio.currentTime = 0;
      setIsPlaying(true);
      lastManualPlayAtRef.current = Date.now();
      registerListeningSession();
      audio.play().catch(err => {
        if (err.name !== 'AbortError') {
          console.error('Playback error:', err);
          setIsPlaying(false);
        }
      });
      return;
    }

    setCurrentPlayingAyah(idx);
    setIsPlaying(true);
    lastManualPlayAtRef.current = Date.now();
    registerListeningSession();
    const url = ayahAudioUrls[idx];
    audio.src = url;
    audio.load();
    audio.play().catch(err => {
      if (err.name !== 'AbortError') {
        console.error('Playback error:', err);
        setIsPlaying(false);
      }
    });
  };

  const handleAyahTap = (idx) => {
    handleAyahLongPressEnd();

    if (touchTapHandledRef.current) {
      touchTapHandledRef.current = false;
      return;
    }

    if (longPressTriggeredRef.current) {
      longPressTriggeredRef.current = false;
      return;
    }

    playAyah(idx);
  };

  const savePreferences = async (updates) => {
    const newPrefs = { ...preferences, ...updates };

    setPreferences(newPrefs);

    try {
      await updateQuranUserPreferences(newPrefs);
    } catch (error) {
      console.error('Error saving preferences:', error);
    }
  };

  const applyMushafPreset = () => {
    setArabicTextWeight('bold');
    setArabicTextSpacing('spacious');
    setUseLegacyMobileMushafLayout(false);

    savePreferences({
      arabic_font_style: 'uthmani',
      font_size: DEFAULT_ARABIC_FONT_SIZE,
      show_arabic: true,
    });

    toast.success(lang === 'hr' ? 'Mushaf preset primijenjen' : 'Mushaf preset applied');
  };

  const jumpToSearchResult = async (result) => {
    const surahNumber = result?.surah?.number;
    const ayahNumber = result?.numberInSurah;
    if (!surahNumber || !ayahNumber) return;

    setSelectedSura(surahNumber);
    setSelectedAya(ayahNumber);
    setShowQuranSearch(false);
    setQuranSearchResults([]);

    await savePreferences({
      reading_mode: 'ayah',
      last_surah: surahNumber,
      last_ayah: ayahNumber,
    });
  };

  const runQuranModuleSearch = async () => {
    const keyword = quranSearchQuery.trim();
    if (!keyword) {
      toast.error(lang === 'hr' ? 'Unesite riječ za pretragu' : 'Enter a search word');
      return;
    }

    setQuranSearching(true);
    try {
      const result = await searchQuranByKeyword(keyword, preferences.preferred_translation || 'bs.korkut');
      setQuranSearchResults(result?.matches || []);

      if (!result?.count) {
        toast.info(lang === 'hr' ? 'Nema rezultata' : 'No results found');
      }
    } catch (error) {
      console.error('Quran module search error:', error);
      toast.error(lang === 'hr' ? 'Greška pri pretrazi' : 'Search failed');
    } finally {
      setQuranSearching(false);
    }
  };

  const toggleAudio = () => {
    if (ayahAudioUrls.length === 0) { toast.error(lang === 'hr' ? 'Audio nije dostupan' : 'Audio not available'); return; }
    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
    } else if (currentPlayingAyah !== null) {
      setIsPlaying(true);
      registerListeningSession();
      audioRef.current?.play().catch(() => {});
    } else {
      const safeIdx = Math.min(Math.max(lastClickedAyahIdx, 0), ayahAudioUrls.length - 1);
      playAyah(safeIdx);
    }
  };

  const markAsRead = async (surahNum) => {
    try {
      const result = await logQuranReadingForBereket(surahNum, 1);
      if (result?.points) toast.success(`+${result.points} ${lang === 'hr' ? 'Bereket bodova!' : 'Bereket points!'}`);
      else if (result?.message === 'Daily limit reached') toast.info(lang === 'hr' ? 'Dostigli ste dnevni limit bodova' : 'Daily limit reached');
    } catch (error) { console.error('Error marking as read:', error); }
  };

  const toggleBookmark = async (surahNum, ayahNum) => {
    const existing = bookmarks.find(b => b.surah_number === surahNum && b.ayah_number === ayahNum);
    if (existing) {
      try {
        await deleteQuranBookmark(existing.id);
        setBookmarks(bookmarks.filter(b => b.id !== existing.id));
        toast.success(lang === 'hr' ? 'Bookmark uklonjen' : 'Bookmark removed');
      } catch (error) { toast.error(lang === 'hr' ? 'Greška' : 'Error'); }
    } else {
      try {
        const bookmark = await addQuranBookmark({ surah_number: surahNum, ayah_number: ayahNum, page_number: null, juz_number: null, note: '' });
        setBookmarks([...bookmarks, bookmark]);
        toast.success(lang === 'hr' ? 'Bookmark dodan' : 'Bookmark added');
      } catch (error) { toast.error(lang === 'hr' ? 'Greška' : 'Error'); }
    }
  };

  // Long-press handlers for ayah actions
  const handleAyahLongPressStart = (surahNum, ayahNum) => {
    longPressTriggeredRef.current = false;

    const ayahIndex = findAyahIndex(surahNum, ayahNum);
    if (ayahIndex >= 0) {
      setLastClickedAyahIdx(ayahIndex);
      const selectedMeta = getAyahMetaByIndex(ayahIndex);
      if (selectedMeta) setActiveAyahMeta(selectedMeta);
    }

    longPressTimer.current = setTimeout(() => {
      longPressTriggeredRef.current = true;
      setLongPressAyah({ surah: surahNum, ayah: ayahNum });
      setShowAyahMenu(true);
    }, 2000);
  };

  const handleAyahContextMenu = (event, surahNum, ayahNum) => {
    event.preventDefault();
    return;
  };

  const handleAyahLongPressEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleAyahTouchEnd = (idx) => {
    handleAyahLongPressEnd();

    if (longPressTriggeredRef.current) {
      longPressTriggeredRef.current = false;
      touchTapHandledRef.current = true;
      setTimeout(() => {
        touchTapHandledRef.current = false;
      }, 250);
      return;
    }

    touchTapHandledRef.current = true;
    playAyah(idx);
    setTimeout(() => {
      touchTapHandledRef.current = false;
    }, 250);
  };

  const markAyahAsRead = async (surahNum, ayahNum) => {
    const ayahIndex = findAyahIndex(surahNum, ayahNum);
    if (ayahIndex >= 0) {
      setLastClickedAyahIdx(ayahIndex);
      const selectedMeta = getAyahMetaByIndex(ayahIndex);
      if (selectedMeta) setActiveAyahMeta(selectedMeta);
    } else {
      const surahName = surahs.find((s) => s.number === surahNum)?.englishName;
      setActiveAyahMeta({
        mode: preferences.reading_mode,
        surahNumber: surahNum,
        surahName,
        ayahNumber: ayahNum,
      });
    }

    const key = `${surahNum}:${ayahNum}`;
    if (readAyahs.has(key)) {
      // Unmark as read - remove from DB
      const newSet = new Set(readAyahs);
      newSet.delete(key);
      setReadAyahs(newSet);
      
      try {
        await unmarkAyahAsReadInDB(surahNum, ayahNum);
        toast.info(lang === 'hr' ? 'Ajet odznačen' : 'Ayah unmarked');
      } catch (error) {
        console.error('Error unmarking ayah:', error);
        toast.info(lang === 'hr' ? 'Ajet odznačen' : 'Ayah unmarked');
      }
    } else {
      // Mark as read - save to DB
      const newSet = new Set(readAyahs);
      newSet.add(key);
      setReadAyahs(newSet);
      
      try {
        // Save to database
        await markAyahAsReadInDB(surahNum, ayahNum);
        
        // Log for Bereket points
        const result = await logQuranReadingForBereket(surahNum, 1);
        if (result?.points) {
          toast.success(`+${result.points} ${lang === 'hr' ? 'Bereket bodova!' : 'Bereket points!'}`);
        } else {
          toast.success(lang === 'hr' ? 'Ajet označen kao pročitan' : 'Ayah marked as read');
        }
      } catch (error) {
        console.error('Error marking ayah:', error);
        toast.success(lang === 'hr' ? 'Ajet označen kao pročitan' : 'Ayah marked as read');
      }
    }
    setShowAyahMenu(false);
  };

  const handleQuickBookmark = async (surahNum, ayahNum) => {
    await toggleBookmark(surahNum, ayahNum);
    setShowAyahMenu(false);
  };

  const isAyahRead = (surahNum, ayahNum) => {
    return readAyahs.has(`${surahNum}:${ayahNum}`);
  };

  const isAyahBookmarked = (surahNum, ayahNum) => {
    return bookmarks.some(b => b.surah_number === surahNum && b.ayah_number === ayahNum);
  };

  const isBookmarked = (surahNum, ayahNum) => bookmarks.some(b => b.surah_number === surahNum && b.ayah_number === ayahNum);

  const goToNextAyah = () => {
    const cur = surahs.find(s => s.number === selectedSura);
    if (!cur) return;
    if (selectedAya < cur.numberOfAyahs) { setSelectedAya(selectedAya + 1); savePreferences({ last_surah: selectedSura, last_ayah: selectedAya + 1 }); }
    else if (selectedSura < 114) { setSelectedSura(selectedSura + 1); setSelectedAya(1); savePreferences({ last_surah: selectedSura + 1, last_ayah: 1 }); }
    setIsPlaying(false);
  };

  const goToPrevAyah = () => {
    if (selectedAya > 1) { setSelectedAya(selectedAya - 1); savePreferences({ last_surah: selectedSura, last_ayah: selectedAya - 1 }); }
    else if (selectedSura > 1) {
      const prev = surahs.find(s => s.number === selectedSura - 1);
      if (prev) { setSelectedSura(selectedSura - 1); setSelectedAya(prev.numberOfAyahs); savePreferences({ last_surah: selectedSura - 1, last_ayah: prev.numberOfAyahs }); }
    }
    setIsPlaying(false);
  };

  const goToNextPage = () => {
    if (selectedPage < 604) {
      const next = selectedPage + 1;
      setSelectedPage(next);
      savePreferences({ reading_mode: 'page' });
      setIsPlaying(false);
    }
  };

  const goToPrevPage = () => {
    if (selectedPage > 1) {
      const prev = selectedPage - 1;
      setSelectedPage(prev);
      savePreferences({ reading_mode: 'page' });
      setIsPlaying(false);
    }
  };

  const touchStartRef = useRef(null);
  const handleSwipeStart = (e) => {
    touchStartRef.current = e.touches[0].clientX;
  };
  const handleSwipeEnd = (e) => {
    if (touchStartRef.current === null) return;
    const diff = touchStartRef.current - e.changedTouches[0].clientX;
    touchStartRef.current = null;
    if (Math.abs(diff) < 60) return;
    if (diff > 0) goToNextPage();
    else goToPrevPage();
  };

  const openRangeSelector = () => {
    const cur = surahs.find(s => s.number === selectedSura);
    if (cur) { 
      setStartAya(1); 
      setEndAya(Math.min(10, cur.numberOfAyahs)); 
      setStartAyaInput('1');
      setEndAyaInput(String(Math.min(10, cur.numberOfAyahs)));
      setShowRangeSelector(true); 
    }
  };

  const openPageSelector = () => {
    setPageInputValue(String(selectedPage));
    setShowPageSelector(true);
  };

  const openJuzSelector = () => {
    setJuzInputValue(String(selectedJuz));
    setShowJuzSelector(true);
  };

  const applyPageSelection = () => {
    const page = parseInt(pageInputValue) || 1;
    const validPage = Math.max(1, Math.min(604, page));
    setSelectedPage(validPage);
    savePreferences({ reading_mode: 'page' });
    setShowPageSelector(false);
  };

  const applyJuzSelection = () => {
    const juz = parseInt(juzInputValue) || 1;
    const validJuz = Math.max(1, Math.min(30, juz));
    setSelectedJuz(validJuz);
    savePreferences({ reading_mode: 'juz' });
    setShowJuzSelector(false);
  };

  const applyRange = () => {
    const start = parseInt(startAyaInput) || 1;
    const end = parseInt(endAyaInput) || 1;
    if (start > end) { 
      toast.error(lang === 'hr' ? 'Početni ajet mora biti manji' : 'Start ayah must be less than end'); 
      return; 
    }
    setStartAya(start);
    setEndAya(end);
    savePreferences({ reading_mode: 'range' });
    setShowRangeSelector(false);
  };

  const currentSurahObj = surahs.find(s => s.number === selectedSura);
  const baseFontSize = preferences.font_size || DEFAULT_ARABIC_FONT_SIZE;
  const arabicOnly = preferences.show_arabic && !preferences.show_translation;
  const hasCustomFontSize = baseFontSize !== DEFAULT_ARABIC_FONT_SIZE;
  const useFullMobileMushafViewport = isMobileViewport && arabicOnly && preferences.reading_mode === 'page' && !useLegacyMobileMushafLayout && !hasCustomFontSize;
  const mobilePageTextDensity = useFullMobileMushafViewport
    ? (pageData?.ayahs || []).reduce((sum, ayah) => sum + (ayah?.text?.length || 0), 0)
    : 0;
  const mobileMushafFontSize = useFullMobileMushafViewport
    ? mobilePageTextDensity > 520
      ? 18.6
      : mobilePageTextDensity > 450
      ? 19.4
      : mobilePageTextDensity > 390
      ? 20.2
      : 21
    : baseFontSize;
  const fontSize = mobileMushafFontSize;

  const spacingMap = {
    compact: { lineHeight: 1.9, wordSpacing: '0.015em' },
    normal: { lineHeight: 2.08, wordSpacing: '0.04em' },
    spacious: { lineHeight: 2.26, wordSpacing: '0.07em' },
  };

  const activeSpacing = spacingMap[arabicTextSpacing] || spacingMap.normal;
  const arabicFontWeightValue = arabicTextWeight === 'bold' ? 600 : 400;

  const getArabicTypographyStyle = (color) => ({
    fontSize: `${fontSize}px`,
    fontWeight: arabicFontWeightValue,
    lineHeight: activeSpacing.lineHeight,
    wordSpacing: activeSpacing.wordSpacing,
    ...(color ? { color } : {}),
  });

  const getMushafTypographyStyle = (fullMobileView = false) => ({
    fontSize: `${fontSize}px`,
    fontWeight: arabicFontWeightValue,
    lineHeight: fullMobileView
      ? Math.max(1.82, Math.min(2.05, activeSpacing.lineHeight - ((fontSize - 18) * 0.012)))
      : activeSpacing.lineHeight,
    wordSpacing: activeSpacing.wordSpacing,
  });

  const fontClassName = (() => {
    switch (preferences.arabic_font_style) {
      case 'uthmani': return 'font-uthmani';
      case 'indopak': return 'font-indopak';
      case 'simple': return 'font-simple';
      case 'naskh': return 'font-naskh';
      default: return 'font-arabic';
    }
  })();

  const arabicEdition = getArabicEditionForStyle(preferences.arabic_font_style);

  const defaultHeaderMeta = (() => {
    if (preferences.reading_mode === 'ayah') {
      return {
        mode: 'ayah',
        surahNumber: selectedSura,
        surahName: currentSurahObj?.englishName,
        ayahNumber: selectedAya,
        pageNumber: ayahData?.page,
      };
    }

    if (preferences.reading_mode === 'range') {
      return {
        mode: 'range',
        surahNumber: selectedSura,
        surahName: currentSurahObj?.englishName,
        rangeStart: startAya,
        rangeEnd: endAya,
        ayahNumber: startAya,
        pageNumber: rangeData?.[0]?.page,
      };
    }

    if (preferences.reading_mode === 'surah') {
      return {
        mode: 'surah',
        surahNumber: selectedSura,
        surahName: currentSurahObj?.englishName,
        ayahNumber: surahData?.ayahs?.[0]?.numberInSurah,
        pageNumber: surahData?.ayahs?.[0]?.page,
      };
    }

    if (preferences.reading_mode === 'page') {
      const firstAyah = pageData?.ayahs?.[0];
      return {
        mode: 'page',
        surahNumber: firstAyah?.surah?.number || selectedSura,
        surahName: firstAyah?.surah?.englishName,
        ayahNumber: firstAyah?.numberInSurah,
        pageNumber: selectedPage,
      };
    }

    if (preferences.reading_mode === 'juz') {
      const firstAyah = juzData?.ayahs?.[0];
      return {
        mode: 'juz',
        surahNumber: firstAyah?.surah?.number,
        surahName: firstAyah?.surah?.englishName,
        ayahNumber: firstAyah?.numberInSurah,
        pageNumber: firstAyah?.page,
        juzNumber: selectedJuz,
      };
    }

    return null;
  })();

  const formatHeaderAyahText = (meta) => {
    if (!meta) return null;

    const surahLabel = meta.surahName || `${lang === 'hr' ? 'Sura' : 'Surah'} ${meta.surahNumber || ''}`.trim();
    const ayahLabel = meta.ayahNumber ? `${lang === 'hr' ? 'Ajet' : 'Ayah'} ${meta.ayahNumber}` : null;
    const pageLabel = meta.pageNumber ? `${lang === 'hr' ? 'Stranica' : 'Page'} ${meta.pageNumber}` : null;
    const rangeLabel = meta.rangeStart && meta.rangeEnd
      ? `${lang === 'hr' ? 'Raspon' : 'Range'} ${meta.rangeStart}-${meta.rangeEnd}`
      : null;

    const parts = [surahLabel];

    if (meta.mode === 'range') {
      if (rangeLabel) parts.push(rangeLabel);
      if (ayahLabel) parts.push(ayahLabel);
      if (pageLabel) parts.push(pageLabel);
      return parts.filter(Boolean).join(' · ');
    }

    if (meta.mode === 'juz') {
      if (pageLabel) parts.push(pageLabel);
      if (ayahLabel) parts.push(ayahLabel);
      return parts.filter(Boolean).join(' · ');
    }

    if (meta.mode === 'page') {
      if (pageLabel) parts.push(pageLabel);
      if (ayahLabel) parts.push(ayahLabel);
      return parts.filter(Boolean).join(' · ');
    }

    if (ayahLabel) parts.push(ayahLabel);
    if (pageLabel && meta.mode === 'surah') parts.push(pageLabel);
    return parts.filter(Boolean).join(' · ');
  };

  const headerMeta = activeAyahMeta || defaultHeaderMeta;
  const headerAyahText = formatHeaderAyahText(headerMeta);
  const focusedHeaderSurahName = headerAyahText
    ? headerAyahText.split(' · ')[0]
    : (headerMeta?.surahName || currentSurahObj?.englishName);

  const openFocusedPrimaryPicker = () => {
    if (preferences.reading_mode === 'page') {
      openPageSelector();
      return;
    }

    if (preferences.reading_mode === 'juz') {
      openJuzSelector();
      return;
    }

    setShowSurahPicker(true);
  };

  const filteredBookmarks = bookmarks.filter((bookmark) => {
    const surah = surahs.find((s) => s.number === bookmark.surah_number);
    const surahName = (surah?.englishName || `Surah ${bookmark.surah_number}`).toLowerCase();
    const query = bookmarkSearchQuery.trim().toLowerCase();
    if (!query) return true;

    return (
      surahName.includes(query) ||
      String(bookmark.surah_number).includes(query) ||
      String(bookmark.ayah_number).includes(query) ||
      `${bookmark.surah_number}:${bookmark.ayah_number}`.includes(query)
    );
  });

  const PageSeparator = ({ page }) => (
    <div className="flex items-center gap-3 my-3 font-body text-xs leading-none" data-testid={`juz-page-separator-${page}`} dir="ltr">
      <div className="flex-1 h-px bg-stone-200" />
      <span className="text-xs font-medium text-stone-400 bg-stone-50 px-3 py-1 rounded-full">
        {lang === 'hr' ? `Stranica ${page}` : `Page ${page}`}
      </span>
      <div className="flex-1 h-px bg-stone-200" />
    </div>
  );

  const AudioWave = () => (
    <span className="inline-flex gap-0.5 items-end mx-2" style={{ height: '14px', verticalAlign: 'middle' }}>
      {[3,5,4,6,3,5,4].map((h, i) => (
        <span key={i} className="bg-emerald-500 rounded-full animate-pulse"
          style={{ width: '2px', height: `${h * 2}px`, animationDelay: `${i * 0.1}s` }} />
      ))}
    </span>
  );

  const NowPlayingBadge = ({ suffix = '' }) => (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wide bg-white/20 text-white"
      data-testid={suffix ? `now-playing-badge-${suffix}` : 'now-playing-badge'}
    >
      {lang === 'hr' ? 'U TOKU' : 'NOW PLAYING'}
    </span>
  );

  const renderAyahBlock = (text, ayahNum, isActive, showTranslation, translationText, surahLabel, surahNum = selectedSura, ayahIdx = null) => {
    const isRead = isAyahRead(surahNum, ayahNum);
    const isBookmarked = isAyahBookmarked(surahNum, ayahNum);
    
    if (arabicOnly) {
      return (
        <span
          className={`transition-all duration-300 inline ${isRead ? 'opacity-60' : ''}`}
          style={{ color: isActive ? '#065f46' : '#1c1917' }}
          onTouchStart={() => handleAyahLongPressStart(surahNum, ayahNum)}
          onTouchEnd={() => {
            if (ayahIdx !== null) handleAyahTouchEnd(ayahIdx);
          }}
          
          
          
        >
          {text}
          <AyahCircle num={ayahNum} />
          {isRead && <Check className="inline w-3 h-3 text-emerald-500 mx-1" />}
          {isBookmarked && <Bookmark className="inline w-3 h-3 text-amber-500 mx-1 fill-amber-500" />}
        </span>
      );
    }

    return (
      <div
        className={`rounded-xl transition-all duration-500 mb-4 overflow-hidden ${
          isActive ? 'ring-2 ring-emerald-400 shadow-lg shadow-emerald-100' : 'border border-stone-100'
        } ${isRead ? 'bg-emerald-50/30' : ''}`}
        onTouchStart={() => handleAyahLongPressStart(surahNum, ayahNum)}
        onTouchEnd={() => {
          if (ayahIdx !== null) handleAyahTouchEnd(ayahIdx);
        }}
        
        
        
      >
        {isActive && (
          <div className="bg-emerald-600 px-3 py-1 flex items-center gap-2">
            <AudioWave />
            <span className="text-xs text-white font-medium">{surahLabel || `${lang === 'hr' ? 'Ajet' : 'Ayah'} ${ayahNum}`}</span>
          </div>
        )}
        {!isActive && (
          <div className="bg-stone-50 border-b border-stone-100 px-3 py-1 flex items-center justify-between">
            <span className="text-xs text-stone-500 font-medium">{surahLabel || `${lang === 'hr' ? 'Ajet' : 'Ayah'} ${ayahNum}`}</span>
            <div className="flex items-center gap-2">
              {isRead && (
                <span className="flex items-center gap-1 text-xs text-emerald-600">
                  <Check className="w-3 h-3" />
                </span>
              )}
              {isBookmarked && (
                <Bookmark className="w-3 h-3 text-amber-500 fill-amber-500" />
              )}
              <button
                onClick={(e) => { e.stopPropagation(); toggleBookmark(surahNum, ayahNum); }}
                className="p-1 hover:bg-stone-200 rounded transition-colors"
                title={lang === 'hr' ? 'Bookmark' : 'Bookmark'}
              >
                <Bookmark className={`w-3.5 h-3.5 ${isBookmarked ? 'text-amber-500 fill-amber-500' : 'text-stone-400'}`} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); markAyahAsRead(surahNum, ayahNum); }}
                className="p-1 hover:bg-stone-200 rounded transition-colors"
                title={lang === 'hr' ? 'Označi kao pročitano' : 'Mark as read'}
              >
                <Check className={`w-3.5 h-3.5 ${isRead ? 'text-emerald-500' : 'text-stone-400'}`} />
              </button>
            </div>
          </div>
        )}
        <div className={`p-4 ${isActive ? 'bg-emerald-50/60' : 'bg-white'}`}>
          {preferences.show_arabic && (
            <p
              className={`leading-[2.1] ${fontClassName} text-right mb-3`}
              dir="rtl"
              style={getArabicTypographyStyle(isActive ? '#065f46' : '#1c1917')}
            >
              {text}
            </p>
          )}
          {showTranslation && translationText && (
            <div className={`rounded-lg p-3 mt-1 ${isActive ? 'bg-white/80' : 'bg-stone-50'}`}>
              <p className={`text-base leading-relaxed ${isActive ? 'text-emerald-900' : 'text-stone-700'}`}>
                {translationText}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  const ArabicOnlyContainer = ({ children }) => (
    <div
      className={`text-right leading-[2.5] ${fontClassName} dark:text-stone-100`}
      dir="rtl"
      style={getArabicTypographyStyle()}
    >
      {children}
    </div>
  );

  const MushafPageContainer = ({ children, pageNum, surahName, juzNum, fullMobileView = false }) => (
    <div className={`mushaf-page ${fullMobileView ? 'mobile-full-view' : ''}`} data-testid="mushaf-page-view">
      {surahName && (
        <div className={`flex items-center justify-between ${fullMobileView ? 'mb-1 px-1' : 'mb-2 px-2'}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
          <span className={`${fullMobileView ? 'text-[11px]' : 'text-xs'} text-stone-500`}>{surahName}</span>
          {juzNum && <span className={`${fullMobileView ? 'text-[11px]' : 'text-xs'} text-stone-500`}>{lang === 'hr' ? `Džuz ${juzNum}` : `Juz ${juzNum}`}</span>}
        </div>
      )}
      <div
        className={`mushaf-text-body ${fontClassName}`}
        style={getMushafTypographyStyle(fullMobileView)}
      >
        {children}
      </div>
      {pageNum && (
        <div className="mushaf-page-number">
          ◁ {pageNum} ▷
        </div>
      )}
    </div>
  );

  const focusedContent = (
    <div className="min-h-screen bg-[#FDFAF5] dark:bg-[#1a1614]">
      <audio ref={audioRef} className="hidden" />

      <div className="sticky top-0 z-10 flex items-center justify-between px-2.5 sm:px-5 py-2 sm:py-3 bg-[#FDFAF5] dark:bg-[#1a1614] border-b border-black/5 dark:border-white/10">
        <div className="flex items-center gap-1.5 sm:gap-3 min-w-0 flex-1">
          <button onClick={openFocusedPrimaryPicker} className="text-xs sm:text-sm font-semibold text-stone-700 dark:text-stone-300 hover:text-emerald-700 dark:hover:text-emerald-400 transition-colors truncate max-w-[110px] sm:max-w-none" data-testid="focused-primary-title-button">
            {focusedHeaderSurahName || 'Select'}
          </button>
          {headerAyahText && (
            <span className="hidden sm:inline-flex items-center gap-1 text-xs text-stone-500 dark:text-stone-400 bg-white/80 dark:bg-stone-800 px-2.5 py-1 rounded-full max-w-[260px] truncate" data-testid="focused-active-ayah-indicator">
              <Hash className="w-3 h-3" />
              {headerAyahText}
            </span>
          )}
          {preferences.reading_mode === 'page' && (
            <div className="flex items-center gap-0.5 sm:gap-1.5" data-testid="focused-page-nav">
              <button onClick={goToPrevPage} disabled={selectedPage <= 1} className="p-1 rounded hover:bg-stone-200 dark:hover:bg-stone-700 disabled:opacity-30 transition-colors" data-testid="focused-prev-page">
                <ChevronLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-stone-600 dark:text-stone-400" />
              </button>
              <button onClick={openPageSelector} className="text-[11px] sm:text-xs font-medium text-stone-500 dark:text-stone-400 hover:text-emerald-600 dark:hover:text-emerald-400 transition-colors tabular-nums" data-testid="focused-page-number">
                <span className="sm:hidden">{selectedPage}</span>
                <span className="hidden sm:inline">{lang === 'hr' ? 'Str.' : 'Page'} {selectedPage}</span>
              </button>
              <button onClick={goToNextPage} disabled={selectedPage >= 604} className="p-1 rounded hover:bg-stone-200 dark:hover:bg-stone-700 disabled:opacity-30 transition-colors" data-testid="focused-next-page">
                <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-stone-600 dark:text-stone-400" />
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 sm:gap-2 pl-1">
          <button
            onClick={() => setShowBookmarks(true)}
            className="p-1.5 rounded-full hover:bg-stone-100 dark:hover:bg-stone-800 text-stone-500 dark:text-stone-400 transition-colors"
            data-testid="focused-open-bookmarks-button"
            title={lang === 'hr' ? 'Bookmarki' : 'Bookmarks'}
          >
            <Bookmark className="w-4 h-4" />
          </button>
          <button
            onClick={() => setShowQuranSearch(true)}
            className="p-1.5 rounded-full hover:bg-stone-100 dark:hover:bg-stone-800 text-stone-500 dark:text-stone-400 transition-colors"
            data-testid="focused-open-quran-search-button"
            title={lang === 'hr' ? 'Pretraga Kur\'ana' : 'Quran Search'}
          >
            <Search className="w-4 h-4" />
          </button>
          <button
            onClick={() => setShowSettings(true)}
            className="p-1.5 rounded-full hover:bg-stone-100 dark:hover:bg-stone-800 text-stone-500 dark:text-stone-400 transition-colors"
            data-testid="focused-open-settings-button"
            title={lang === 'hr' ? 'Postavke' : 'Settings'}
          >
            <Settings className="w-4 h-4" />
          </button>
          {ayahAudioUrls.length > 0 && (
            <button
              onClick={toggleAudio}
              className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all ${
                isPlaying ? 'bg-emerald-600 text-white' : 'bg-white dark:bg-stone-800 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800 hover:border-emerald-400'
              }`}
              data-testid="focused-toggle-audio-button"
            >
              {isPlaying ? <Pause className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">{isPlaying ? (lang === 'hr' ? 'Pauziraj' : 'Pause') : (lang === 'hr' ? 'Slušaj' : 'Listen')}</span>
            </button>
          )}
          <button onClick={() => savePreferences({ focused_mode: false })} className="p-1.5 rounded-full hover:bg-stone-100 dark:hover:bg-stone-800 text-stone-400 transition-colors" data-testid="focused-exit-mode-button">
            <Minimize2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className={`max-w-xl mx-auto px-3 sm:px-5 ${useFullMobileMushafViewport ? 'py-2' : 'py-8'}`}>
        {degradedNotice && (
          <div
            className="mb-3 sm:mb-4 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs sm:text-sm text-amber-900 flex items-start gap-2"
            data-testid="quran-degraded-mode-banner"
          >
            <Layers className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{degradedNotice}</span>
          </div>
        )}
        {loading ? (
          <div className="flex justify-center py-32">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-emerald-500 border-t-transparent" />
          </div>
        ) : (
          <>
            {preferences.reading_mode === 'ayah' && ayahData && (
              <div>
                {arabicOnly ? (
                  <div
                    ref={isPlaying ? activeAyahRef : null}
                    className={`rounded-2xl p-6 transition-all duration-500 ${isPlaying ? 'bg-emerald-50/70 dark:bg-emerald-900/30 ring-1 ring-emerald-200 dark:ring-emerald-700' : ''}`}
                  >
                    {isPlaying && <div className="flex justify-center mb-3"><AudioWave /></div>}
                    <ArabicOnlyContainer>
                      <span className={isPlaying ? 'text-emerald-800 dark:text-emerald-300' : 'text-stone-900 dark:text-stone-100'}>
                        {ayahData.text}
                        <AyahCircle num={selectedAya} />
                      </span>
                    </ArabicOnlyContainer>
                  </div>
                ) : (
                  <div className={`rounded-2xl overflow-hidden transition-all duration-500 ${isPlaying ? 'ring-2 ring-emerald-400 shadow-lg shadow-emerald-100 dark:shadow-emerald-900/50' : 'border border-stone-200 dark:border-stone-700'}`}>
                    {isPlaying && (
                      <div className="bg-emerald-600 px-4 py-2 flex items-center gap-2">
                        <AudioWave />
                        <span className="text-xs text-white font-medium">{lang === 'hr' ? 'Ajet' : 'Ayah'} {selectedAya}</span>
                      </div>
                    )}
                    <div className={`p-5 ${isPlaying ? 'bg-emerald-50/60 dark:bg-emerald-900/30' : 'bg-white dark:bg-stone-800'}`}>
                      {preferences.show_arabic && (
                        <p className={`${fontClassName} text-right leading-[2.1] mb-4`} dir="rtl"
                          style={getArabicTypographyStyle(isPlaying ? '#065f46' : '#1c1917')}>
                          {ayahData.text}
                        </p>
                      )}
                      {preferences.show_translation && (
                        <div className={`rounded-lg p-3 ${isPlaying ? 'bg-white/80' : 'bg-stone-50'}`}>
                          <p className={`text-base leading-relaxed ${isPlaying ? 'text-emerald-900' : 'text-stone-700'}`}>{ayahData.translation}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div className="flex items-center justify-center gap-5 mt-6">
                  <button onClick={goToPrevAyah} disabled={selectedSura === 1 && selectedAya === 1}
                    className="p-2.5 rounded-full bg-white border border-stone-200 hover:border-stone-300 disabled:opacity-30 shadow-sm transition-all">
                    <ChevronRight className="w-5 h-5 text-stone-600" />
                  </button>
                  <button onClick={() => markAsRead(selectedSura)} className="p-2 rounded-full text-stone-400 hover:text-emerald-600 transition-colors">
                    <BookCheck className="w-5 h-5" />
                  </button>
                  <button onClick={() => toggleBookmark(selectedSura, selectedAya)}
                    className={`p-2 rounded-full transition-colors ${isBookmarked(selectedSura, selectedAya) ? 'text-amber-500' : 'text-stone-400 hover:text-amber-500'}`}>
                    {isBookmarked(selectedSura, selectedAya) ? <BookmarkCheck className="w-5 h-5" /> : <Bookmark className="w-5 h-5" />}
                  </button>
                  <button onClick={goToNextAyah} disabled={selectedSura === 114 && currentSurahObj && selectedAya === currentSurahObj.numberOfAyahs}
                    className="p-2.5 rounded-full bg-white border border-stone-200 hover:border-stone-300 disabled:opacity-30 shadow-sm transition-all">
                    <ChevronLeft className="w-5 h-5 text-stone-600" />
                  </button>
                </div>
              </div>
            )}

            {preferences.reading_mode === 'surah' && surahData && (
              arabicOnly ? (
                <MushafPageContainer surahName={surahData.englishName || currentSurahObj?.englishName}>
                  {surahData.ayahs.map((ayah, idx) => {
                    const isActive = currentPlayingAyah === idx && isPlaying;
                    const currentPage = ayah.page || ayah.pageNumber;
                    const prevPage = idx > 0 ? (surahData.ayahs[idx - 1]?.page || surahData.ayahs[idx - 1]?.pageNumber) : null;
                    const showPageBreak = Boolean(currentPage) && (idx === 0 || currentPage !== prevPage);
                    return (
                      <span key={ayah.numberInSurah}>
                        {showPageBreak && <PageSeparator page={currentPage} />}
                        <span ref={isActive ? activeAyahRef : null}
                          onClick={() => handleAyahTap(idx)}
                          onTouchStart={() => handleAyahLongPressStart(selectedSura, ayah.numberInSurah)}
                          onTouchEnd={() => handleAyahTouchEnd(idx)}
                          onContextMenu={(e) => handleAyahContextMenu(e, selectedSura, ayah.numberInSurah)}
                          className="transition-all duration-300 inline cursor-pointer hover:text-emerald-700"
                          style={{ color: isActive ? '#065f46' : '#1c1917', textShadow: isActive ? '0 0 12px rgba(6,95,70,0.2)' : 'none' }}>
                          {ayah.text}
                          <MushafAyahMarker num={ayah.numberInSurah} />
                          {' '}
                        </span>
                      </span>
                    );
                  })}
                </MushafPageContainer>
              ) : (
                <div>
                  {surahData.ayahs.map((ayah, idx) => {
                    const isActive = currentPlayingAyah === idx && isPlaying;
                    const currentPage = ayah.page || ayah.pageNumber;
                    const prevPage = idx > 0 ? (surahData.ayahs[idx - 1]?.page || surahData.ayahs[idx - 1]?.pageNumber) : null;
                    const showPageBreak = Boolean(currentPage) && (idx === 0 || currentPage !== prevPage);
                    return (
                      <div key={ayah.numberInSurah}>
                        {showPageBreak && <PageSeparator page={currentPage} />}
                        <div ref={isActive ? activeAyahRef : null}
                          onTouchStart={() => handleAyahLongPressStart(selectedSura, ayah.numberInSurah)}
                          onTouchEnd={() => handleAyahTouchEnd(idx)}
                          onContextMenu={(e) => handleAyahContextMenu(e, selectedSura, ayah.numberInSurah)}
                          className={`rounded-xl mb-3 overflow-hidden transition-all duration-500 ${isActive ? 'ring-2 ring-emerald-400 shadow-lg shadow-emerald-100' : 'border border-stone-100'}`}>
                          <div onClick={() => handleAyahTap(idx)}
                            className={`flex items-center justify-between px-3 py-1.5 cursor-pointer ${isActive ? 'bg-emerald-600' : 'bg-stone-50 border-b border-stone-100'}`}>
                            <div className="flex items-center gap-2">
                              {isActive && <AudioWave />}
                              {isActive && <NowPlayingBadge suffix={`focus-surah-${idx}`} />}
                              <span className={`text-xs font-medium ${isActive ? 'text-white' : 'text-stone-400'}`}>{lang === 'hr' ? 'Ajet' : 'Ayah'} {ayah.numberInSurah}{currentPage ? ` · ${lang === 'hr' ? 'str.' : 'p.'} ${currentPage}` : ''}</span>
                            </div>
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center ${isActive ? 'bg-white/20' : 'bg-stone-200'}`}>
                              {isActive ? <Pause className="w-2.5 h-2.5 text-white" /> : <Play className="w-2.5 h-2.5 text-stone-600" />}
                            </div>
                          </div>
                          <div className={`p-4 ${isActive ? 'bg-emerald-50/60' : 'bg-white'}`}>
                            {preferences.show_arabic && (
                              <p className={`${fontClassName} text-right leading-[2.1] mb-3`} dir="rtl"
                                style={getArabicTypographyStyle(isActive ? '#065f46' : '#1c1917')}>
                                {ayah.text}
                              </p>
                            )}
                            {preferences.show_translation && (
                              <div className={`rounded-lg p-3 ${isActive ? 'bg-white/80' : 'bg-stone-50'}`}>
                                <p className={`text-sm leading-relaxed ${isActive ? 'text-emerald-900' : 'text-stone-600'}`}>{ayah.translation}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            )}

            {preferences.reading_mode === 'range' && rangeData.length > 0 && (
              arabicOnly ? (
                <ArabicOnlyContainer>
                  {rangeData.map((ayah, idx) => {
                    const isActive = currentPlayingAyah === idx && isPlaying;
                    const ayahNum = startAya + idx;
                    return (
                      <span key={idx} ref={isActive ? activeAyahRef : null}
                        onClick={() => handleAyahTap(idx)}
                        onTouchStart={() => handleAyahLongPressStart(selectedSura, ayahNum)}
                        onTouchEnd={() => handleAyahTouchEnd(idx)}
                        
                        
                        
                        onContextMenu={(e) => handleAyahContextMenu(e, selectedSura, ayahNum)}
                        className="transition-all duration-300 inline cursor-pointer hover:text-emerald-700"
                        style={{ color: isActive ? '#065f46' : '#1c1917', textShadow: isActive ? '0 0 12px rgba(6,95,70,0.2)' : 'none' }}>
                        {ayah.text}
                        <MushafAyahMarker num={ayahNum} />
                        {' '}
                      </span>
                    );
                  })}
                </ArabicOnlyContainer>
              ) : (
                <div>
                  {rangeData.map((ayah, idx) => {
                    const isActive = currentPlayingAyah === idx && isPlaying;
                    const ayahNum = startAya + idx;
                    return (
                      <div key={idx} ref={isActive ? activeAyahRef : null}
                        onTouchStart={() => handleAyahLongPressStart(selectedSura, ayahNum)}
                        onTouchEnd={() => handleAyahTouchEnd(idx)}
                        onContextMenu={(e) => handleAyahContextMenu(e, selectedSura, ayahNum)}
                        className={`rounded-xl mb-3 overflow-hidden transition-all duration-500 ${isActive ? 'ring-2 ring-emerald-400 shadow-lg shadow-emerald-100' : 'border border-stone-100'}`}>
                        <div onClick={() => handleAyahTap(idx)}
                          className={`flex items-center justify-between px-3 py-1.5 cursor-pointer ${isActive ? 'bg-emerald-600' : 'bg-stone-50 border-b border-stone-100'}`}>
                          <div className="flex items-center gap-2">
                            {isActive && <AudioWave />}
                            {isActive && <NowPlayingBadge suffix={`focus-${idx}`} />}
                            <span className={`text-xs font-medium ${isActive ? 'text-white' : 'text-stone-400'}`}>{lang === 'hr' ? 'Ajet' : 'Ayah'} {ayahNum}</span>
                          </div>
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center ${isActive ? 'bg-white/20' : 'bg-stone-200'}`}>
                            {isActive ? <Pause className="w-2.5 h-2.5 text-white" /> : <Play className="w-2.5 h-2.5 text-stone-600" />}
                          </div>
                        </div>
                        <div className={`p-4 ${isActive ? 'bg-emerald-50/60' : 'bg-white'}`}>
                          {preferences.show_arabic && <p className={`${fontClassName} text-right leading-[2.1] mb-3`} dir="rtl" style={getArabicTypographyStyle(isActive ? '#065f46' : '#1c1917')}>{ayah.text}</p>}
                          {preferences.show_translation && <div className={`rounded-lg p-3 ${isActive ? 'bg-white/80' : 'bg-stone-50'}`}><p className={`text-sm leading-relaxed ${isActive ? 'text-emerald-900' : 'text-stone-600'}`}>{ayah.translation}</p></div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            )}

            {preferences.reading_mode === 'page' && pageData && (
              <div onTouchStart={handleSwipeStart} onTouchEnd={handleSwipeEnd} data-testid="page-swipe-container">
              {arabicOnly ? (
                <MushafPageContainer
                  pageNum={selectedPage}
                  surahName={pageData.ayahs?.[0]?.surah?.englishName}
                  juzNum={pageData.ayahs?.[0]?.juz}
                  fullMobileView={useFullMobileMushafViewport}
                >
                  {pageData.ayahs.map((ayah, idx) => {
                    const isActive = currentPlayingAyah === idx && isPlaying;
                    const surahNum = ayah.surah?.number || ayah.surahNumber;
                    return (
                      <span key={idx} ref={isActive ? activeAyahRef : null}
                        onClick={() => handleAyahTap(idx)}
                        onTouchStart={() => handleAyahLongPressStart(surahNum, ayah.numberInSurah)}
                        onTouchEnd={() => handleAyahTouchEnd(idx)}
                        onContextMenu={(e) => handleAyahContextMenu(e, surahNum, ayah.numberInSurah)}
                        className="transition-all duration-300 inline cursor-pointer hover:text-emerald-700"
                        style={{ color: isActive ? '#065f46' : '#1c1917', textShadow: isActive ? '0 0 12px rgba(6,95,70,0.2)' : 'none' }}>
                        {ayah.text}
                        <MushafAyahMarker num={ayah.numberInSurah} />
                        {' '}
                      </span>
                    );
                  })}
                </MushafPageContainer>
              ) : (
                <div>
                  {pageData.ayahs.map((ayah, idx) => {
                    const isActive = currentPlayingAyah === idx && isPlaying;
                    const surahNum = ayah.surah?.number || ayah.surahNumber;
                    return (
                      <div key={idx} ref={isActive ? activeAyahRef : null}
                        onTouchStart={() => handleAyahLongPressStart(surahNum, ayah.numberInSurah)}
                        onTouchEnd={() => handleAyahTouchEnd(idx)}
                        onContextMenu={(e) => handleAyahContextMenu(e, surahNum, ayah.numberInSurah)}
                        className={`rounded-xl mb-3 overflow-hidden transition-all duration-500 ${isActive ? 'ring-2 ring-emerald-400 shadow-lg shadow-emerald-100' : 'border border-stone-100'}`}>
                        <div onClick={() => handleAyahTap(idx)}
                          className={`flex items-center justify-between px-3 py-1.5 cursor-pointer ${isActive ? 'bg-emerald-600' : 'bg-stone-50 border-b border-stone-100'}`}>
                          <div className="flex items-center gap-2">
                            {isActive && <AudioWave />}
                            {isActive && <NowPlayingBadge suffix={`focus-${idx}`} />}
                            <span className={`text-xs font-medium ${isActive ? 'text-white' : 'text-stone-400'}`}>{ayah.surah.englishName} {ayah.numberInSurah}</span>
                          </div>
                          <div className={`w-5 h-5 rounded-full flex items-center justify-center ${isActive ? 'bg-white/20' : 'bg-stone-200'}`}>
                            {isActive ? <Pause className="w-2.5 h-2.5 text-white" /> : <Play className="w-2.5 h-2.5 text-stone-600" />}
                          </div>
                        </div>
                        <div className={`p-4 ${isActive ? 'bg-emerald-50/60' : 'bg-white'}`}>
                          {preferences.show_arabic && <p className={`${fontClassName} text-right leading-[2.1] mb-3`} dir="rtl" style={getArabicTypographyStyle(isActive ? '#065f46' : '#1c1917')}>{ayah.text}</p>}
                          {preferences.show_translation && ayah.translation && <div className={`rounded-lg p-3 ${isActive ? 'bg-white/80' : 'bg-stone-50'}`}><p className={`text-sm leading-relaxed ${isActive ? 'text-emerald-900' : 'text-stone-600'}`}>{ayah.translation}</p></div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              </div>
            )}

            {preferences.reading_mode === 'juz' && juzData && (
              arabicOnly ? (
                <ArabicOnlyContainer>
                  {juzData.ayahs.map((ayah, idx) => {
                    const isActive = currentPlayingAyah === idx && isPlaying;
                    const surahNum = ayah.surah?.number || ayah.surahNumber;
                    const currentPage = ayah.page || ayah.pageNumber;
                    const prevPage = idx > 0 ? (juzData.ayahs[idx - 1]?.page || juzData.ayahs[idx - 1]?.pageNumber) : null;
                    const showPageBreak = Boolean(currentPage) && (idx === 0 || currentPage !== prevPage);
                    return (
                      <span key={idx}>
                        {showPageBreak && (
                          <PageSeparator page={currentPage} />
                        )}
                        <span ref={isActive ? activeAyahRef : null}
                          onClick={() => handleAyahTap(idx)}
                          onTouchStart={() => handleAyahLongPressStart(surahNum, ayah.numberInSurah)}
                          onTouchEnd={() => handleAyahTouchEnd(idx)}
                          
                          
                          
                          onContextMenu={(e) => handleAyahContextMenu(e, surahNum, ayah.numberInSurah)}
                          className="transition-all duration-300 inline cursor-pointer hover:text-emerald-700"
                          style={{ color: isActive ? '#065f46' : '#1c1917', textShadow: isActive ? '0 0 12px rgba(6,95,70,0.2)' : 'none' }}>
                          {ayah.text}
                          <MushafAyahMarker num={ayah.numberInSurah} />
                          {' '}
                        </span>
                      </span>
                    );
                  })}
                </ArabicOnlyContainer>
              ) : (
                <div>
                  {juzData.ayahs.map((ayah, idx) => {
                    const isActive = currentPlayingAyah === idx && isPlaying;
                    const surahNum = ayah.surah?.number || ayah.surahNumber;
                    const currentPage = ayah.page || ayah.pageNumber;
                    const prevPage = idx > 0 ? (juzData.ayahs[idx - 1]?.page || juzData.ayahs[idx - 1]?.pageNumber) : null;
                    const showPageBreak = Boolean(currentPage) && (idx === 0 || currentPage !== prevPage);
                    return (
                      <div key={idx}>
                        {showPageBreak && (
                          <PageSeparator page={currentPage} />
                        )}
                        <div ref={isActive ? activeAyahRef : null}
                          onTouchStart={() => handleAyahLongPressStart(surahNum, ayah.numberInSurah)}
                          onTouchEnd={() => handleAyahTouchEnd(idx)}
                          onContextMenu={(e) => handleAyahContextMenu(e, surahNum, ayah.numberInSurah)}
                          className={`rounded-xl mb-3 overflow-hidden transition-all duration-500 ${isActive ? 'ring-2 ring-emerald-400 shadow-lg shadow-emerald-100' : 'border border-stone-100'}`}>
                          <div onClick={() => handleAyahTap(idx)}
                            className={`flex items-center justify-between px-3 py-1.5 cursor-pointer ${isActive ? 'bg-emerald-600' : 'bg-stone-50 border-b border-stone-100'}`}>
                            <div className="flex items-center gap-2">
                              {isActive && <AudioWave />}
                              {isActive && <NowPlayingBadge suffix={`focus-juz-${idx}`} />}
                              <span className={`text-xs font-medium ${isActive ? 'text-white' : 'text-stone-400'}`}>{ayah.surah.englishName} {ayah.numberInSurah}</span>
                              {currentPage && (
                                <span className={`text-xs ${isActive ? 'text-white/60' : 'text-stone-300'}`}>· {lang === 'hr' ? 'str.' : 'p.'} {currentPage}</span>
                              )}
                            </div>
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center ${isActive ? 'bg-white/20' : 'bg-stone-200'}`}>
                              {isActive ? <Pause className="w-2.5 h-2.5 text-white" /> : <Play className="w-2.5 h-2.5 text-stone-600" />}
                            </div>
                          </div>
                          <div className={`p-4 ${isActive ? 'bg-emerald-50/60' : 'bg-white'}`}>
                            {preferences.show_arabic && <p className={`${fontClassName} text-right leading-[2.1] mb-3`} dir="rtl" style={getArabicTypographyStyle(isActive ? '#065f46' : '#1c1917')}>{ayah.text}</p>}
                            {preferences.show_translation && ayah.translation && <div className={`rounded-lg p-3 ${isActive ? 'bg-white/80' : 'bg-stone-50'}`}><p className={`text-sm leading-relaxed ${isActive ? 'text-emerald-900' : 'text-stone-600'}`}>{ayah.translation}</p></div>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            )}
          </>
        )}
      </div>
    </div>
  );

  const standardAyahList = (ayahs, getAyahNum, getLabel, getTranslation, getAyahText, opts = {}) => {
    if (arabicOnly) {
      const pageNum = opts.pageNum;
      const surahName = opts.surahName;
      const juzNum = opts.juzNum;
      const fullMobileView = opts.fullMobileView;
      const useMushaf = opts.mushafStyle !== false;
      const showPageSeparators = Boolean(opts.showPageSeparators && opts.getPageNumber);
      const getPageNumber = opts.getPageNumber || (() => null);

      const content = ayahs.map((ayah, idx) => {
        const isActive = currentPlayingAyah === idx && isPlaying;
        const num = getAyahNum(ayah, idx);
        const surahNum = ayah.surah?.number || ayah.surahNumber || selectedSura;
        const isRead = isAyahRead(surahNum, num);
        const isBkmk = isAyahBookmarked(surahNum, num);
        const currentPage = getPageNumber(ayah, idx);
        const previousPage = idx > 0 ? getPageNumber(ayahs[idx - 1], idx - 1) : null;
        const showPageBreak = showPageSeparators && currentPage && (idx === 0 || currentPage !== previousPage);
        return (
          <span key={idx}>
            {showPageBreak && <PageSeparator page={currentPage} />}
            <span
              ref={isActive ? activeAyahRef : null}
              onClick={() => handleAyahTap(idx)}
              onTouchStart={() => handleAyahLongPressStart(surahNum, num)}
              onTouchEnd={() => handleAyahTouchEnd(idx)}
              
              
              
              onContextMenu={(e) => handleAyahContextMenu(e, surahNum, num)}
              className={`transition-all duration-300 inline cursor-pointer hover:text-emerald-700 dark:hover:text-emerald-400 ${isRead ? 'opacity-60' : ''}`}
              style={{
                ...getArabicTypographyStyle(isActive ? '#065f46' : undefined),
                textShadow: isActive ? '0 0 15px rgba(6,95,70,0.25)' : 'none',
                fontSize: isActive ? `${fontSize + 1}px` : `${fontSize}px`,
              }}>
              {getAyahText(ayah)}
              {useMushaf ? <MushafAyahMarker num={num} /> : <AyahCircle num={num} />}
              {isRead && <Check className="inline w-3 h-3 text-emerald-500 mx-1" />}
              {isBkmk && <Bookmark className="inline w-3 h-3 text-amber-500 mx-1 fill-amber-500" />}
              {' '}
            </span>
          </span>
        );
      });

      if (useMushaf) {
        return (
          <MushafPageContainer pageNum={pageNum} surahName={surahName} juzNum={juzNum} fullMobileView={fullMobileView}>
            {content}
          </MushafPageContainer>
        );
      }

      return (
        <div className="rounded-2xl p-6 bg-stone-50/50 dark:bg-stone-800/30">
          <div className={`${fontClassName} text-right leading-[2.5]`} dir="rtl" style={getArabicTypographyStyle()}>
            {content}
          </div>
        </div>
      );
    }

    const showPageSeparators = Boolean(opts.showPageSeparators && opts.getPageNumber);
    const getPageNumber = opts.getPageNumber || (() => null);

    return (
      <div className="space-y-3">
        {ayahs.map((ayah, idx) => {
          const isActive = currentPlayingAyah === idx && isPlaying;
          const isPaused = currentPlayingAyah === idx && !isPlaying;
          const num = getAyahNum(ayah, idx);
          const surahNum = ayah.surah?.number || ayah.surahNumber || selectedSura;
          const label = getLabel ? getLabel(ayah, idx) : `${lang === 'hr' ? 'Ajet' : 'Ayah'} ${num}`;
          const translation = getTranslation(ayah);
          const isRead = isAyahRead(surahNum, num);
          const isBookmarked = isAyahBookmarked(surahNum, num);
          const currentPage = getPageNumber(ayah, idx);
          const previousPage = idx > 0 ? getPageNumber(ayahs[idx - 1], idx - 1) : null;
          const showPageBreak = showPageSeparators && currentPage && (idx === 0 || currentPage !== previousPage);
          return (
            <div key={idx}>
              {showPageBreak && <PageSeparator page={currentPage} />}
              <div
                ref={isActive ? activeAyahRef : null}
                className={`rounded-xl overflow-hidden transition-all duration-500 ${
                  isActive
                    ? 'ring-2 ring-emerald-400 shadow-lg shadow-emerald-100 dark:shadow-emerald-900/50'
                    : isPaused
                    ? 'ring-1 ring-emerald-200 dark:ring-emerald-700 bg-emerald-50/30 dark:bg-emerald-900/20'
                    : 'border border-stone-100 dark:border-stone-700'
                } ${isRead ? 'bg-emerald-50/30 dark:bg-emerald-900/20' : ''}`}
                onTouchStart={() => handleAyahLongPressStart(surahNum, num)}
                onTouchEnd={() => handleAyahTouchEnd(idx)}
                onContextMenu={(e) => handleAyahContextMenu(e, surahNum, num)}
              >
                <div
                  className={`flex items-center justify-between px-3 py-1.5 cursor-pointer ${
                    isActive ? 'bg-emerald-600' : isPaused ? 'bg-emerald-50 dark:bg-emerald-900/30' : 'bg-stone-50 dark:bg-stone-800/50 border-b border-stone-100 dark:border-stone-700'
                  }`}
                  onClick={() => handleAyahTap(idx)}
                >
                  <div className="flex items-center gap-2">
                    {isActive && <AudioWave />}
                    {isActive && <NowPlayingBadge suffix={`standard-${idx}`} />}
                    <span className={`text-xs font-medium ${isActive ? 'text-white' : isPaused ? 'text-emerald-700 dark:text-emerald-400' : 'text-stone-400 dark:text-stone-500'}`}>{label}</span>
                    {isRead && !isActive && <Check className="w-3 h-3 text-emerald-500" />}
                    {isBookmarked && !isActive && <Bookmark className="w-3 h-3 text-amber-500 fill-amber-500" />}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleBookmark(surahNum, num); }}
                      className={`p-1 rounded transition-colors ${isActive ? 'hover:bg-white/20' : 'hover:bg-stone-200 dark:hover:bg-stone-600'}`}
                    >
                      <Bookmark className={`w-3.5 h-3.5 ${isBookmarked ? 'text-amber-500 fill-amber-500' : isActive ? 'text-white/70' : 'text-stone-400'}`} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); markAyahAsRead(surahNum, num); }}
                      className={`p-1 rounded transition-colors ${isActive ? 'hover:bg-white/20' : 'hover:bg-stone-200 dark:hover:bg-stone-600'}`}
                    >
                      <Check className={`w-3.5 h-3.5 ${isRead ? 'text-emerald-500' : isActive ? 'text-white/70' : 'text-stone-400'}`} />
                    </button>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                      isActive ? 'bg-white/20' : 'bg-stone-200 dark:bg-stone-600 hover:bg-emerald-100 dark:hover:bg-emerald-800'
                    }`}>
                      {isActive
                        ? <Pause className="w-3 h-3 text-white" />
                        : <Play className="w-3 h-3 text-stone-600 dark:text-stone-300" />
                      }
                    </div>
                  </div>
                </div>
                <div className={`p-4 ${isActive ? 'bg-emerald-50/60 dark:bg-emerald-900/30' : 'bg-white dark:bg-stone-800'}`}>
                  {preferences.show_arabic && (
                    <p className={`${fontClassName} text-right leading-[2.1] dark:text-stone-100`} dir="rtl"
                      style={{
                        ...getArabicTypographyStyle(isActive ? '#065f46' : undefined),
                        marginBottom: preferences.show_translation && translation ? '12px' : '0',
                      }}>
                      {getAyahText(ayah)}
                    </p>
                  )}
                  {preferences.show_translation && translation && (
                    <div className={`rounded-lg p-3 ${isActive ? 'bg-white/80 dark:bg-stone-700/50' : 'bg-stone-50 dark:bg-stone-700/30'}`}>
                      <p className={`text-sm leading-relaxed ${isActive ? 'text-emerald-900 dark:text-emerald-200' : 'text-stone-700 dark:text-stone-300'}`}>{translation}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const standardContent = (
    <>
      <audio ref={audioRef} className="hidden" />
      <div className="bg-gradient-to-br from-emerald-50 via-white to-teal-50 min-h-screen">
        <div className="max-w-4xl mx-auto p-4 md:p-8">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b border-stone-200 px-3 sm:px-4 py-2.5">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-100 text-emerald-700">
                    <Book className="w-4 h-4" />
                  </span>
                  <span className="text-sm font-semibold text-stone-800 truncate">{lang === 'hr' ? "Kur'an" : 'Quran'}</span>
                  {headerAyahText && (
                    <span className="hidden sm:inline-flex items-center gap-1 text-xs text-stone-500 bg-stone-100 px-2 py-1 rounded-full max-w-[300px] truncate" data-testid="standard-active-ayah-indicator">
                      <Hash className="w-3 h-3" />
                      {headerAyahText}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => setShowBookmarks(!showBookmarks)} className="p-1.5 sm:p-2 bg-stone-100 hover:bg-stone-200 rounded-lg transition-all" data-testid="standard-open-bookmarks-button"><Bookmark className="w-4 h-4 text-stone-700" /></button>
                  <button onClick={() => setShowQuranSearch(true)} className="p-1.5 sm:p-2 bg-stone-100 hover:bg-stone-200 rounded-lg transition-all" data-testid="standard-open-quran-search-button"><Search className="w-4 h-4 text-stone-700" /></button>
                  <button onClick={() => setShowSettings(!showSettings)} className="p-1.5 sm:p-2 bg-stone-100 hover:bg-stone-200 rounded-lg transition-all" data-testid="standard-open-settings-button"><Settings className="w-4 h-4 text-stone-700" /></button>
                </div>
              </div>
            </div>

            {showSettings && (
              <div className="border-b border-stone-200 bg-stone-50 p-6 space-y-4">
                <h3 className="font-semibold text-lg text-stone-800">{lang === 'hr' ? 'Postavke' : 'Settings'}</h3>
                <div className="flex items-center justify-between">
                  <span className="text-stone-700">{lang === 'hr' ? 'Način čitanja' : 'Reading Mode'}</span>
                  <div className="flex gap-2 flex-wrap">
                    {[
                      { key: 'ayah', label: lang === 'hr' ? 'Ajet' : 'Ayah', action: () => savePreferences({ reading_mode: 'ayah' }) },
                      { key: 'range', label: lang === 'hr' ? 'Raspon' : 'Range', action: openRangeSelector },
                      { key: 'surah', label: lang === 'hr' ? 'Sura' : 'Surah', action: () => savePreferences({ reading_mode: 'surah' }) },
                      { key: 'page', label: lang === 'hr' ? 'Stranica' : 'Page', action: openPageSelector },
                      { key: 'juz', label: lang === 'hr' ? 'Džuz' : 'Juz', action: openJuzSelector },
                    ].map(m => (
                      <button key={m.key} onClick={m.action} className={`px-3 py-2 rounded-lg transition-all text-sm ${preferences.reading_mode === m.key ? 'bg-emerald-600 text-white' : 'bg-white text-stone-700 hover:bg-stone-100'}`}>{m.label}</button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-stone-700">{lang === 'hr' ? 'Prikaži arapski' : 'Show Arabic'}</span>
                  <button onClick={() => savePreferences({ show_arabic: !preferences.show_arabic })}>
                    {preferences.show_arabic ? <Eye className="w-5 h-5 text-emerald-600" /> : <EyeOff className="w-5 h-5 text-stone-400" />}
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-stone-700">{lang === 'hr' ? 'Prikaži prijevod' : 'Show Translation'}</span>
                  <button onClick={() => savePreferences({ show_translation: !preferences.show_translation })}>
                    {preferences.show_translation ? <Eye className="w-5 h-5 text-emerald-600" /> : <EyeOff className="w-5 h-5 text-stone-400" />}
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-stone-700">{lang === 'hr' ? 'Prijevod' : 'Translation'}</span>
                  <button onClick={() => setShowTranslationPicker(true)} className="px-4 py-2 bg-white rounded-lg hover:bg-stone-100 text-stone-700 text-sm">
                    {translations.find(t => t.identifier === preferences.preferred_translation)?.name || 'Select'}
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-stone-700">{lang === 'hr' ? 'Recitator' : 'Reciter'}</span>
                  <button onClick={() => setShowReciterPicker(true)} className="px-4 py-2 bg-white rounded-lg hover:bg-stone-100 text-stone-700 text-sm">
                    {reciters.find(r => r.identifier === preferences.preferred_reciter)?.name || 'Select'}
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-stone-700">{lang === 'hr' ? 'Stil pisma' : 'Font Style'}</span>
                  <button onClick={() => setShowFontStylePicker(true)} className="px-4 py-2 bg-white rounded-lg hover:bg-stone-100 text-stone-700 text-sm">
                    {fontStyles.find(f => f.value === preferences.arabic_font_style)?.label || 'Select'}
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-stone-700">{lang === 'hr' ? 'Veličina fonta' : 'Font Size'}</span>
                  <input
                    type="range"
                    min="16"
                    max="48"
                    value={preferences.font_size}
                    onChange={(e) => {
                      setUseLegacyMobileMushafLayout(true);
                      savePreferences({ font_size: parseInt(e.target.value, 10) });
                    }}
                    className="w-32"
                  />
                  <span className="text-sm text-stone-500">{preferences.font_size}px</span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-stone-700">{lang === 'hr' ? 'Debljina arapskog teksta' : 'Arabic Text Weight'}</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setArabicTextWeight('normal')}
                      className={`px-3 py-1.5 rounded-lg text-sm transition-all ${arabicTextWeight === 'normal' ? 'bg-emerald-600 text-white' : 'bg-white text-stone-700 hover:bg-stone-100'}`}
                      data-testid="standard-arabic-weight-normal"
                    >
                      {lang === 'hr' ? 'Normalno' : 'Normal'}
                    </button>
                    <button
                      onClick={() => setArabicTextWeight('bold')}
                      className={`px-3 py-1.5 rounded-lg text-sm transition-all ${arabicTextWeight === 'bold' ? 'bg-emerald-600 text-white' : 'bg-white text-stone-700 hover:bg-stone-100'}`}
                      data-testid="standard-arabic-weight-bold"
                    >
                      {lang === 'hr' ? 'Pojačano' : 'Bold'}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-stone-700">{lang === 'hr' ? 'Razmak teksta' : 'Text Spacing'}</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setArabicTextSpacing('compact')}
                      className={`px-2.5 py-1.5 rounded-lg text-sm transition-all ${arabicTextSpacing === 'compact' ? 'bg-emerald-600 text-white' : 'bg-white text-stone-700 hover:bg-stone-100'}`}
                      data-testid="standard-arabic-spacing-compact"
                    >
                      {lang === 'hr' ? 'Manji' : 'Compact'}
                    </button>
                    <button
                      onClick={() => setArabicTextSpacing('normal')}
                      className={`px-2.5 py-1.5 rounded-lg text-sm transition-all ${arabicTextSpacing === 'normal' ? 'bg-emerald-600 text-white' : 'bg-white text-stone-700 hover:bg-stone-100'}`}
                      data-testid="standard-arabic-spacing-normal"
                    >
                      {lang === 'hr' ? 'Srednji' : 'Normal'}
                    </button>
                    <button
                      onClick={() => setArabicTextSpacing('spacious')}
                      className={`px-2.5 py-1.5 rounded-lg text-sm transition-all ${arabicTextSpacing === 'spacious' ? 'bg-emerald-600 text-white' : 'bg-white text-stone-700 hover:bg-stone-100'}`}
                      data-testid="standard-arabic-spacing-spacious"
                    >
                      {lang === 'hr' ? 'Veći' : 'Spacious'}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-stone-700">{lang === 'hr' ? 'Mushaf preset' : 'Mushaf Preset'}</span>
                  <button
                    onClick={applyMushafPreset}
                    className="px-3 py-1.5 rounded-lg text-sm bg-emerald-600 text-white hover:bg-emerald-700 transition-all"
                    data-testid="standard-apply-mushaf-preset"
                  >
                    {lang === 'hr' ? 'Primijeni' : 'Apply'}
                  </button>
                </div>
              </div>
            )}

            {showBookmarks && (
              <div className="border-b border-stone-200 bg-amber-50 p-6">
                <h3 className="font-semibold text-lg text-stone-800 mb-4 flex items-center gap-2"><Bookmark className="w-5 h-5" />{lang === 'hr' ? 'Bookmarks' : 'Bookmarks'}</h3>
                {bookmarks.length === 0 ? (
                  <p className="text-stone-600 text-center py-4">{lang === 'hr' ? 'Nemate bookmarke' : 'No bookmarks yet'}</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {bookmarks.map((bookmark) => {
                      const surah = surahs.find(s => s.number === bookmark.surah_number);
                      return (
                        <div key={bookmark.id} className="flex items-center justify-between bg-white p-3 rounded-lg">
                          <button onClick={() => { setSelectedSura(bookmark.surah_number); setSelectedAya(bookmark.ayah_number); savePreferences({ reading_mode: 'ayah', last_surah: bookmark.surah_number, last_ayah: bookmark.ayah_number }); setShowBookmarks(false); }} className="flex-1 text-left">
                            <span className="font-medium text-emerald-600">{surah?.englishName || `Surah ${bookmark.surah_number}`}</span>
                            <span className="text-stone-600 ml-2">- {lang === 'hr' ? 'Ajet' : 'Ayah'} {bookmark.ayah_number}</span>
                          </button>
                          <button onClick={() => toggleBookmark(bookmark.surah_number, bookmark.ayah_number)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all"><X className="w-4 h-4" /></button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <div className="p-6">
              {degradedNotice && (
                <div
                  className="mb-4 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs sm:text-sm text-amber-900 flex items-start gap-2"
                  data-testid="quran-degraded-mode-banner"
                >
                  <Layers className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{degradedNotice}</span>
                </div>
              )}
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent mx-auto mb-4" />
                    <p className="text-stone-600">{lang === 'hr' ? 'Učitavanje...' : 'Loading...'}</p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-3 pb-3 border-b border-stone-200 gap-2">
                    <div className="flex items-center gap-2">
                      {preferences.reading_mode !== 'page' && preferences.reading_mode !== 'juz' && (
                        <button onClick={() => setShowSurahPicker(true)} className="px-3 sm:px-4 py-1.5 sm:py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all text-xs sm:text-sm font-medium">
                          {currentSurahObj ? `${currentSurahObj.number}. ${currentSurahObj.englishName}` : 'Select Surah'}
                        </button>
                      )}
                      {preferences.reading_mode === 'page' && (
                        <button onClick={openPageSelector} className="px-3 sm:px-4 py-1.5 sm:py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all text-xs sm:text-sm font-medium">
                          {lang === 'hr' ? 'Stranica' : 'Page'} {selectedPage}
                        </button>
                      )}
                      {preferences.reading_mode === 'juz' && (
                        <button onClick={openJuzSelector} className="px-3 sm:px-4 py-1.5 sm:py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all text-xs sm:text-sm font-medium">
                          {lang === 'hr' ? 'Džuz' : 'Juz'} {selectedJuz}
                        </button>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {preferences.reading_mode === 'ayah' && (
                        <div className="flex items-center gap-2 sm:gap-3">
                          <button onClick={goToPrevAyah} disabled={selectedSura === 1 && selectedAya === 1} className="p-2 bg-stone-100 hover:bg-stone-200 rounded-lg disabled:opacity-50 transition-all"><ChevronLeft className="w-4 h-4" /></button>
                          <span className="text-sm sm:text-base font-semibold text-stone-700">{lang === 'hr' ? 'Ajet' : 'Ayah'} {selectedAya}</span>
                          <button onClick={goToNextAyah} disabled={selectedSura === 114 && currentSurahObj && selectedAya === currentSurahObj.numberOfAyahs} className="p-2 bg-stone-100 hover:bg-stone-200 rounded-lg disabled:opacity-50 transition-all"><ChevronRight className="w-4 h-4" /></button>
                        </div>
                      )}
                      {preferences.reading_mode === 'page' && (
                        <div className="flex items-center gap-2 sm:gap-3" data-testid="desktop-page-nav">
                          <button onClick={goToPrevPage} disabled={selectedPage <= 1} className="p-2 bg-stone-100 hover:bg-stone-200 rounded-lg disabled:opacity-50 transition-all" data-testid="desktop-prev-page"><ChevronLeft className="w-4 h-4" /></button>
                          <button onClick={openPageSelector} className="text-sm sm:text-base font-semibold text-stone-700 hover:text-emerald-600 transition-colors tabular-nums" data-testid="desktop-page-number">{lang === 'hr' ? 'Str.' : 'Page'} {selectedPage}</button>
                          <button onClick={goToNextPage} disabled={selectedPage >= 604} className="p-2 bg-stone-100 hover:bg-stone-200 rounded-lg disabled:opacity-50 transition-all" data-testid="desktop-next-page"><ChevronRight className="w-4 h-4" /></button>
                        </div>
                      )}
                      {preferences.reading_mode === 'range' && (
                        <div className="text-sm sm:text-base font-semibold text-stone-700">{lang === 'hr' ? 'Ajeti' : 'Ayahs'} {startAya}-{endAya}</div>
                      )}
                      {ayahAudioUrls.length > 0 && (
                        <button onClick={toggleAudio} className={`flex items-center gap-1 px-2.5 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all ${isPlaying ? 'bg-emerald-700 text-white' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`} data-testid="standard-toggle-audio-button">
                          {isPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                          <span className="hidden sm:inline">{isPlaying ? (lang === 'hr' ? 'Pauziraj' : 'Pause') : (lang === 'hr' ? 'Slušaj' : 'Play')}</span>
                        </button>
                      )}
                      <button onClick={() => savePreferences({ focused_mode: true })} className="p-2 bg-stone-100 hover:bg-stone-200 rounded-lg transition-all" title={lang === 'hr' ? 'Fokusirani mod' : 'Focused mode'} data-testid="standard-enter-focused-mode-button">
                        <Maximize2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {preferences.reading_mode === 'ayah' && ayahData && (
                    arabicOnly ? (
                      <div className={`rounded-2xl p-6 transition-all duration-500 ${isPlaying ? 'bg-emerald-50 ring-2 ring-emerald-300' : 'bg-stone-50'}`}>
                        {isPlaying && <div className="flex justify-center mb-3"><AudioWave /></div>}
                        <div className={`${fontClassName} text-right leading-[2.5]`} dir="rtl"
                          style={getArabicTypographyStyle(isPlaying ? '#065f46' : '#1c1917')}>
                          {ayahData.text}
                          <AyahCircle num={selectedAya} />
                        </div>
                        <div className="flex justify-end gap-2 mt-4">
                          <button onClick={() => markAsRead(selectedSura)} className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all shadow" title={lang === 'hr' ? 'Označi kao pročitano' : 'Mark as Read'}><BookCheck className="w-4 h-4" /></button>
                          <button onClick={() => toggleBookmark(selectedSura, selectedAya)} className={`p-2 rounded-lg transition-all shadow ${isBookmarked(selectedSura, selectedAya) ? 'bg-amber-600 text-white' : 'bg-amber-100 text-amber-700 hover:bg-amber-200'}`}>
                            {isBookmarked(selectedSura, selectedAya) ? <BookmarkCheck className="w-4 h-4" /> : <Bookmark className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className={`rounded-2xl overflow-hidden transition-all duration-500 ${isPlaying ? 'ring-2 ring-emerald-400 shadow-lg shadow-emerald-100' : 'border border-stone-200'}`}>
                          {isPlaying ? (
                            <div className="bg-emerald-600 px-4 py-2 flex items-center gap-2">
                              <AudioWave />
                              <span className="text-sm text-white font-medium">{lang === 'hr' ? 'Ajet' : 'Ayah'} {selectedAya}</span>
                            </div>
                          ) : (
                            <div className="bg-stone-50 border-b border-stone-100 px-4 py-1.5">
                              <span className="text-xs text-stone-400">{lang === 'hr' ? 'Ajet' : 'Ayah'} {selectedAya}</span>
                            </div>
                          )}
                          <div className={`p-5 ${isPlaying ? 'bg-emerald-50/60' : 'bg-white'}`}>
                            {preferences.show_arabic && (
                              <p className={`${fontClassName} text-right leading-[2.1]`} dir="rtl"
                                style={{
                                  ...getArabicTypographyStyle(isPlaying ? '#065f46' : '#1c1917'),
                                  marginBottom: preferences.show_translation ? '16px' : 0,
                                }}>
                                {ayahData.text}
                              </p>
                            )}
                            {preferences.show_translation && (
                              <div className={`rounded-xl p-4 ${isPlaying ? 'bg-white/80' : 'bg-stone-50'}`}>
                                <p className={`text-lg leading-relaxed ${isPlaying ? 'text-emerald-900' : 'text-stone-700'}`}>{ayahData.translation}</p>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex justify-end gap-2">
                          <button onClick={() => markAsRead(selectedSura)} className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all shadow-lg"><BookCheck className="w-5 h-5" /></button>
                          <button onClick={() => toggleBookmark(selectedSura, selectedAya)} className={`p-2 rounded-lg transition-all shadow-lg ${isBookmarked(selectedSura, selectedAya) ? 'bg-amber-600 text-white hover:bg-amber-700' : 'bg-amber-100 text-amber-700 hover:bg-amber-200'}`}>
                            {isBookmarked(selectedSura, selectedAya) ? <BookmarkCheck className="w-5 h-5" /> : <Bookmark className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>
                    )
                  )}

                  {preferences.reading_mode === 'range' && rangeData.length > 0 && standardAyahList(
                    rangeData,
                    (_, idx) => startAya + idx,
                    (_, idx) => `${lang === 'hr' ? 'Ajet' : 'Ayah'} ${startAya + idx}`,
                    (ayah) => ayah.translation,
                    (ayah) => ayah.text,
                    { mushafStyle: true, surahName: currentSurahObj?.englishName }
                  )}

                  {preferences.reading_mode === 'surah' && surahData && standardAyahList(
                    surahData.ayahs,
                    (ayah) => ayah.numberInSurah,
                    (ayah) => `${lang === 'hr' ? 'Ajet' : 'Ayah'} ${ayah.numberInSurah}${ayah.page ? ` · ${lang === 'hr' ? 'str.' : 'p.'} ${ayah.page}` : ''}`,
                    (ayah) => ayah.translation,
                    (ayah) => ayah.text,
                    {
                      mushafStyle: true,
                      surahName: surahData.englishName || currentSurahObj?.englishName,
                      showPageSeparators: true,
                      getPageNumber: (ayah) => ayah.page || ayah.pageNumber,
                    }
                  )}

                  {preferences.reading_mode === 'page' && pageData && (
                    <div onTouchStart={handleSwipeStart} onTouchEnd={handleSwipeEnd} data-testid="standard-page-swipe-container">
                      {standardAyahList(
                        pageData.ayahs,
                        (ayah) => ayah.numberInSurah,
                        (ayah) => `${ayah.surah.englishName} ${ayah.numberInSurah}`,
                        (ayah) => ayah.translation,
                        (ayah) => ayah.text,
                        {
                          mushafStyle: true,
                          pageNum: selectedPage,
                          surahName: pageData.surahs?.[Object.keys(pageData.surahs || {})[0]]?.englishName || pageData.ayahs?.[0]?.surah?.englishName,
                          juzNum: pageData.ayahs?.[0]?.juz,
                          fullMobileView: useFullMobileMushafViewport,
                        }
                      )}
                    </div>
                  )}

                  {preferences.reading_mode === 'juz' && juzData && (
                    <>
                      {standardAyahList(
                        juzData.ayahs,
                        (ayah) => ayah.numberInSurah,
                        (ayah) => `${ayah.surah.englishName} ${ayah.numberInSurah} · ${lang === 'hr' ? 'str.' : 'p.'} ${ayah.page || ''}`,
                        (ayah) => ayah.translation,
                        (ayah) => ayah.text,
                        {
                          mushafStyle: true,
                          juzNum: selectedJuz,
                          surahName: juzData.ayahs?.[0]?.surah?.englishName,
                          showPageSeparators: true,
                          getPageNumber: (ayah) => ayah.page || ayah.pageNumber,
                        }
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const modals = (
    <>
      <ModalPicker isOpen={showSurahPicker} onClose={() => setShowSurahPicker(false)} title={lang === 'hr' ? 'Odaberi Suru' : 'Select Surah'} items={surahs} selectedValue={selectedSura}
        onSelect={(value) => { setSelectedSura(value); setSelectedAya(1); savePreferences({ last_surah: value, last_ayah: 1 }); }}
        searchable searchPlaceholder={lang === 'hr' ? 'Pretraži sure...' : 'Search surahs...'}
        renderItem={(surah) => <div><div className="font-medium">{surah.number}. {surah.englishName}</div><div className="text-sm text-stone-500">{surah.name} - {surah.numberOfAyahs} {lang === 'hr' ? 'ajeta' : 'ayahs'}</div></div>}
      />
      <ModalPicker isOpen={showReciterPicker} onClose={() => setShowReciterPicker(false)} title={lang === 'hr' ? 'Odaberi Recitatora' : 'Select Reciter'} items={reciters} selectedValue={preferences.preferred_reciter}
        onSelect={(value) => savePreferences({ preferred_reciter: value })} searchable searchPlaceholder={lang === 'hr' ? 'Pretraži recitatore...' : 'Search reciters...'}
        renderItem={(r) => <div><div className="font-medium">{r.name}</div><div className="text-sm text-stone-500">{r.style}</div></div>}
      />
      <ModalPicker isOpen={showTranslationPicker} onClose={() => setShowTranslationPicker(false)} title={lang === 'hr' ? 'Odaberi Prijevod' : 'Select Translation'} items={translations} selectedValue={preferences.preferred_translation}
        onSelect={(value) => savePreferences({ preferred_translation: value })} searchable searchPlaceholder={lang === 'hr' ? 'Pretraži prijevode...' : 'Search translations...'}
        renderItem={(t) => <div><div className="font-medium">{t.name}</div><div className="text-sm text-stone-500">{t.language}</div></div>}
      />
      <ModalPicker isOpen={showFontStylePicker} onClose={() => setShowFontStylePicker(false)} title={lang === 'hr' ? 'Odaberi Stil Pisma' : 'Select Font Style'} items={fontStyles} selectedValue={preferences.arabic_font_style}
        onSelect={(value) => savePreferences({ arabic_font_style: value })}
        renderItem={(s) => <div><div className="font-medium">{s.label}</div><div className="text-sm text-stone-500">{s.description}</div></div>}
      />

      {showSettings && preferences.focused_mode && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm" onClick={() => setShowSettings(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="focused-settings-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg text-stone-800">{lang === 'hr' ? 'Postavke' : 'Settings'}</h3>
              <button onClick={() => setShowSettings(false)} className="p-1 rounded-lg hover:bg-stone-100" data-testid="focused-settings-close-button">
                <X className="w-4 h-4 text-stone-500" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-stone-700">{lang === 'hr' ? 'Način čitanja' : 'Reading Mode'}</span>
                <div className="flex gap-2 flex-wrap justify-end">
                  {[
                    { key: 'ayah', label: lang === 'hr' ? 'Ajet' : 'Ayah', action: () => savePreferences({ reading_mode: 'ayah' }) },
                    { key: 'range', label: lang === 'hr' ? 'Raspon' : 'Range', action: openRangeSelector },
                    { key: 'surah', label: lang === 'hr' ? 'Sura' : 'Surah', action: () => savePreferences({ reading_mode: 'surah' }) },
                    { key: 'page', label: lang === 'hr' ? 'Stranica' : 'Page', action: openPageSelector },
                    { key: 'juz', label: lang === 'hr' ? 'Džuz' : 'Juz', action: openJuzSelector },
                  ].map((mode) => (
                    <button
                      key={mode.key}
                      onClick={mode.action}
                      className={`px-3 py-2 rounded-lg transition-all text-sm ${preferences.reading_mode === mode.key ? 'bg-emerald-600 text-white' : 'bg-stone-100 text-stone-700 hover:bg-stone-200'}`}
                      data-testid={`focused-settings-mode-${mode.key}`}
                    >
                      {mode.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-stone-700">{lang === 'hr' ? 'Prikaži arapski' : 'Show Arabic'}</span>
                <button onClick={() => savePreferences({ show_arabic: !preferences.show_arabic })} data-testid="focused-settings-toggle-arabic">
                  {preferences.show_arabic ? <Eye className="w-5 h-5 text-emerald-600" /> : <EyeOff className="w-5 h-5 text-stone-400" />}
                </button>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-stone-700">{lang === 'hr' ? 'Prikaži prijevod' : 'Show Translation'}</span>
                <button onClick={() => savePreferences({ show_translation: !preferences.show_translation })} data-testid="focused-settings-toggle-translation">
                  {preferences.show_translation ? <Eye className="w-5 h-5 text-emerald-600" /> : <EyeOff className="w-5 h-5 text-stone-400" />}
                </button>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-stone-700">{lang === 'hr' ? 'Prijevod' : 'Translation'}</span>
                <button onClick={() => setShowTranslationPicker(true)} className="px-4 py-2 bg-stone-100 rounded-lg hover:bg-stone-200 text-stone-700 text-sm" data-testid="focused-settings-open-translation-picker">
                  {translations.find(t => t.identifier === preferences.preferred_translation)?.name || 'Select'}
                </button>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-stone-700">{lang === 'hr' ? 'Recitator' : 'Reciter'}</span>
                <button onClick={() => setShowReciterPicker(true)} className="px-4 py-2 bg-stone-100 rounded-lg hover:bg-stone-200 text-stone-700 text-sm" data-testid="focused-settings-open-reciter-picker">
                  {reciters.find(r => r.identifier === preferences.preferred_reciter)?.name || 'Select'}
                </button>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-stone-700">{lang === 'hr' ? 'Stil pisma' : 'Font Style'}</span>
                <button onClick={() => setShowFontStylePicker(true)} className="px-4 py-2 bg-stone-100 rounded-lg hover:bg-stone-200 text-stone-700 text-sm" data-testid="focused-settings-open-font-picker">
                  {fontStyles.find(f => f.value === preferences.arabic_font_style)?.label || 'Select'}
                </button>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-stone-700">{lang === 'hr' ? 'Veličina fonta' : 'Font Size'}</span>
                <input
                  type="range"
                  min="16"
                  max="48"
                  value={preferences.font_size}
                  onChange={(e) => {
                    setUseLegacyMobileMushafLayout(true);
                    savePreferences({ font_size: parseInt(e.target.value, 10) });
                  }}
                  className="w-36"
                  data-testid="focused-settings-font-size-slider"
                />
                <span className="text-sm text-stone-500">{preferences.font_size}px</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-stone-700">{lang === 'hr' ? 'Debljina arapskog teksta' : 'Arabic Text Weight'}</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setArabicTextWeight('normal')}
                    className={`px-3 py-1.5 rounded-lg text-sm transition-all ${arabicTextWeight === 'normal' ? 'bg-emerald-600 text-white' : 'bg-stone-100 text-stone-700 hover:bg-stone-200'}`}
                    data-testid="focused-arabic-weight-normal"
                  >
                    {lang === 'hr' ? 'Normalno' : 'Normal'}
                  </button>
                  <button
                    onClick={() => setArabicTextWeight('bold')}
                    className={`px-3 py-1.5 rounded-lg text-sm transition-all ${arabicTextWeight === 'bold' ? 'bg-emerald-600 text-white' : 'bg-stone-100 text-stone-700 hover:bg-stone-200'}`}
                    data-testid="focused-arabic-weight-bold"
                  >
                    {lang === 'hr' ? 'Pojačano' : 'Bold'}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-stone-700">{lang === 'hr' ? 'Razmak teksta' : 'Text Spacing'}</span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setArabicTextSpacing('compact')}
                    className={`px-2.5 py-1.5 rounded-lg text-sm transition-all ${arabicTextSpacing === 'compact' ? 'bg-emerald-600 text-white' : 'bg-stone-100 text-stone-700 hover:bg-stone-200'}`}
                    data-testid="focused-arabic-spacing-compact"
                  >
                    {lang === 'hr' ? 'Manji' : 'Compact'}
                  </button>
                  <button
                    onClick={() => setArabicTextSpacing('normal')}
                    className={`px-2.5 py-1.5 rounded-lg text-sm transition-all ${arabicTextSpacing === 'normal' ? 'bg-emerald-600 text-white' : 'bg-stone-100 text-stone-700 hover:bg-stone-200'}`}
                    data-testid="focused-arabic-spacing-normal"
                  >
                    {lang === 'hr' ? 'Srednji' : 'Normal'}
                  </button>
                  <button
                    onClick={() => setArabicTextSpacing('spacious')}
                    className={`px-2.5 py-1.5 rounded-lg text-sm transition-all ${arabicTextSpacing === 'spacious' ? 'bg-emerald-600 text-white' : 'bg-stone-100 text-stone-700 hover:bg-stone-200'}`}
                    data-testid="focused-arabic-spacing-spacious"
                  >
                    {lang === 'hr' ? 'Veći' : 'Spacious'}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-stone-700">{lang === 'hr' ? 'Mushaf preset' : 'Mushaf Preset'}</span>
                <button
                  onClick={applyMushafPreset}
                  className="px-3 py-1.5 rounded-lg text-sm bg-emerald-600 text-white hover:bg-emerald-700 transition-all"
                  data-testid="focused-apply-mushaf-preset"
                >
                  {lang === 'hr' ? 'Primijeni' : 'Apply'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showQuranSearch && (
        <div className="fixed inset-0 z-[75] flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm" onClick={() => setShowQuranSearch(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="quran-header-search-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg text-stone-800 flex items-center gap-2">
                <Search className="w-5 h-5" />
                {lang === 'hr' ? 'Pretraga Kur\'ana' : 'Quran Search'}
              </h3>
              <button onClick={() => setShowQuranSearch(false)} className="p-1 rounded-lg hover:bg-stone-100" data-testid="quran-header-search-close-button">
                <X className="w-4 h-4 text-stone-500" />
              </button>
            </div>

            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={quranSearchQuery}
                onChange={(e) => setQuranSearchQuery(e.target.value)}
                placeholder={lang === 'hr' ? 'Upiši riječ ili frazu...' : 'Type a word or phrase...'}
                className="flex-1 px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                data-testid="quran-header-search-input"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    runQuranModuleSearch();
                  }
                }}
              />
              <button
                onClick={runQuranModuleSearch}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-all"
                data-testid="quran-header-search-submit-button"
              >
                {quranSearching ? (lang === 'hr' ? 'Tražim...' : 'Searching...') : (lang === 'hr' ? 'Traži' : 'Search')}
              </button>
            </div>

            {quranSearchResults.length === 0 ? (
              <p className="text-sm text-stone-500 py-4 text-center" data-testid="quran-header-search-empty-state">
                {lang === 'hr' ? 'Nema rezultata. Probaj drugu riječ.' : 'No results yet. Try another keyword.'}
              </p>
            ) : (
              <div className="space-y-2 max-h-[58vh] overflow-y-auto" data-testid="quran-header-search-results-list">
                {quranSearchResults.map((result, index) => (
                  <button
                    key={`${result.number}-${index}`}
                    onClick={() => jumpToSearchResult(result)}
                    className="w-full text-left p-3 border border-stone-200 rounded-lg hover:border-emerald-300 hover:bg-emerald-50 transition-all"
                    data-testid={`quran-header-search-result-${result.surah?.number || 0}-${result.numberInSurah || 0}`}
                  >
                    <div className="text-xs text-emerald-700 font-semibold mb-1">
                      {result.surah?.englishName} · {lang === 'hr' ? 'Ajet' : 'Ayah'} {result.numberInSurah}
                    </div>
                    <div className="text-sm text-stone-700 line-clamp-2">{result.text}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {showBookmarks && preferences.focused_mode && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm" onClick={() => setShowBookmarks(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="focused-bookmarks-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg text-stone-800 flex items-center gap-2">
                <Bookmark className="w-5 h-5" />
                {lang === 'hr' ? 'Bookmarki' : 'Bookmarks'}
              </h3>
              <button onClick={() => setShowBookmarks(false)} className="p-1 rounded-lg hover:bg-stone-100" data-testid="focused-bookmarks-close-button">
                <X className="w-4 h-4 text-stone-500" />
              </button>
            </div>

            <div className="relative mb-4">
              <Search className="w-4 h-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={bookmarkSearchQuery}
                onChange={(e) => setBookmarkSearchQuery(e.target.value)}
                placeholder={lang === 'hr' ? 'Pretraži suru ili ajet...' : 'Search surah or ayah...'}
                className="w-full pl-9 pr-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                data-testid="focused-bookmarks-search-input"
              />
            </div>

            {filteredBookmarks.length === 0 ? (
              <p className="text-stone-600 text-center py-8" data-testid="focused-bookmarks-empty-state">
                {lang === 'hr' ? 'Nema bookmarka' : 'No bookmarks'}
              </p>
            ) : (
              <div className="space-y-2 max-h-[50vh] overflow-y-auto" data-testid="focused-bookmarks-list">
                {filteredBookmarks.map((bookmark) => {
                  const surah = surahs.find((s) => s.number === bookmark.surah_number);
                  return (
                    <div key={bookmark.id} className="flex items-center justify-between bg-stone-50 p-3 rounded-lg border border-stone-100">
                      <button
                        onClick={() => {
                          setSelectedSura(bookmark.surah_number);
                          setSelectedAya(bookmark.ayah_number);
                          savePreferences({ reading_mode: 'ayah', last_surah: bookmark.surah_number, last_ayah: bookmark.ayah_number });
                          setShowBookmarks(false);
                        }}
                        className="flex-1 text-left"
                        data-testid={`focused-bookmark-jump-${bookmark.surah_number}-${bookmark.ayah_number}`}
                      >
                        <span className="font-medium text-emerald-600">{surah?.englishName || `Surah ${bookmark.surah_number}`}</span>
                        <span className="text-stone-600 ml-2">- {lang === 'hr' ? 'Ajet' : 'Ayah'} {bookmark.ayah_number}</span>
                      </button>
                      <button
                        onClick={() => toggleBookmark(bookmark.surah_number, bookmark.ayah_number)}
                        className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-all"
                        data-testid={`focused-bookmark-remove-${bookmark.surah_number}-${bookmark.ayah_number}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {showPageSelector && (
        <div className="fixed inset-0 z-[85] flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm" onClick={() => setShowPageSelector(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-semibold text-stone-800 mb-4">{lang === 'hr' ? 'Odaberi Stranicu' : 'Select Page'}</h3>
            <input 
              type="number" 
              min="1" 
              max="604" 
              value={pageInputValue} 
              onChange={(e) => setPageInputValue(e.target.value)}
              onBlur={(e) => {
                if (e.target.value === '') setPageInputValue('1');
              }}
              className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 mb-4" 
              placeholder={lang === 'hr' ? 'Unesite broj stranice (1-604)' : 'Enter page number (1-604)'} 
            />
            <div className="flex gap-3">
              <button onClick={() => setShowPageSelector(false)} className="flex-1 px-4 py-2 bg-stone-200 text-stone-700 rounded-lg hover:bg-stone-300">{lang === 'hr' ? 'Odustani' : 'Cancel'}</button>
              <button onClick={applyPageSelection} className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">{lang === 'hr' ? 'Primijeni' : 'Apply'}</button>
            </div>
          </div>
        </div>
      )}

      {showJuzSelector && (
        <div className="fixed inset-0 z-[85] flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm" onClick={() => setShowJuzSelector(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-semibold text-stone-800 mb-4">{lang === 'hr' ? 'Odaberi Džuz' : 'Select Juz'}</h3>
            <input 
              type="number" 
              min="1" 
              max="30" 
              value={juzInputValue} 
              onChange={(e) => setJuzInputValue(e.target.value)}
              onBlur={(e) => {
                if (e.target.value === '') setJuzInputValue('1');
              }}
              className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500 mb-4" 
              placeholder={lang === 'hr' ? 'Unesite broj džuza (1-30)' : 'Enter juz number (1-30)'} 
            />
            <div className="flex gap-3">
              <button onClick={() => setShowJuzSelector(false)} className="flex-1 px-4 py-2 bg-stone-200 text-stone-700 rounded-lg hover:bg-stone-300">{lang === 'hr' ? 'Odustani' : 'Cancel'}</button>
              <button onClick={applyJuzSelection} className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">{lang === 'hr' ? 'Primijeni' : 'Apply'}</button>
            </div>
          </div>
        </div>
      )}

      {showRangeSelector && currentSurahObj && (
        <div className="fixed inset-0 z-[85] flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm" onClick={() => setShowRangeSelector(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-semibold text-stone-800 mb-4">{lang === 'hr' ? 'Odaberi Raspon Ajeta' : 'Select Ayah Range'}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">{lang === 'hr' ? 'Od ajeta' : 'From Ayah'}</label>
                <input 
                  type="number" 
                  min="1" 
                  max={currentSurahObj.numberOfAyahs} 
                  value={startAyaInput} 
                  onChange={(e) => setStartAyaInput(e.target.value)}
                  onBlur={(e) => {
                    if (e.target.value === '') setStartAyaInput('1');
                  }}
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500" 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">{lang === 'hr' ? 'Do ajeta' : 'To Ayah'}</label>
                <input 
                  type="number" 
                  min="1" 
                  max={currentSurahObj.numberOfAyahs} 
                  value={endAyaInput} 
                  onChange={(e) => setEndAyaInput(e.target.value)}
                  onBlur={(e) => {
                    if (e.target.value === '') setEndAyaInput('1');
                  }}
                  className="w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-emerald-500" 
                />
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={() => setShowRangeSelector(false)} className="flex-1 px-4 py-2 bg-stone-200 text-stone-700 rounded-lg hover:bg-stone-300">{lang === 'hr' ? 'Odustani' : 'Cancel'}</button>
                <button onClick={applyRange} className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700">{lang === 'hr' ? 'Primijeni' : 'Apply'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Ayah Long-Press Menu */}
      {showAyahMenu && longPressAyah && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm" 
          onClick={() => setShowAyahMenu(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-xs p-4" 
            onClick={e => e.stopPropagation()}
          >
            <div className="text-center mb-4">
              <p className="text-sm text-stone-500">
                {lang === 'hr' ? 'Ajet' : 'Ayah'} {longPressAyah.ayah}
              </p>
              <p className="text-xs text-stone-400">
                {surahs.find(s => s.number === longPressAyah.surah)?.englishName}
              </p>
            </div>
            
            <div className="space-y-2">
              <button
                onClick={() => markAyahAsRead(longPressAyah.surah, longPressAyah.ayah)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  isAyahRead(longPressAyah.surah, longPressAyah.ayah)
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-stone-50 text-stone-700 hover:bg-emerald-50'
                }`}
              >
                <Check className={`w-5 h-5 ${isAyahRead(longPressAyah.surah, longPressAyah.ayah) ? 'text-emerald-600' : ''}`} />
                <span className="font-medium">
                  {isAyahRead(longPressAyah.surah, longPressAyah.ayah)
                    ? (lang === 'hr' ? 'Pročitano ✓' : 'Read ✓')
                    : (lang === 'hr' ? 'Označi kao pročitano' : 'Mark as read')
                  }
                </span>
              </button>
              
              <button
                onClick={() => handleQuickBookmark(longPressAyah.surah, longPressAyah.ayah)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                  isAyahBookmarked(longPressAyah.surah, longPressAyah.ayah)
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-stone-50 text-stone-700 hover:bg-amber-50'
                }`}
              >
                <Bookmark className={`w-5 h-5 ${isAyahBookmarked(longPressAyah.surah, longPressAyah.ayah) ? 'text-amber-600 fill-amber-600' : ''}`} />
                <span className="font-medium">
                  {isAyahBookmarked(longPressAyah.surah, longPressAyah.ayah)
                    ? (lang === 'hr' ? 'Ukloni bookmark' : 'Remove bookmark')
                    : (lang === 'hr' ? 'Dodaj bookmark' : 'Add bookmark')
                  }
                </span>
              </button>
            </div>
            
            <button
              onClick={() => setShowAyahMenu(false)}
              className="w-full mt-4 px-4 py-2 text-sm text-stone-500 hover:text-stone-700"
            >
              {lang === 'hr' ? 'Zatvori' : 'Close'}
            </button>
          </div>
        </div>
      )}
    </>
  );

  if (preferences.focused_mode) {
    return <>{focusedContent}{modals}</>;
  }

  return <Layout user={user}>{standardContent}{modals}</Layout>;
}
